<template>
  <v-card-text>

    <!-- ---------------------------- Date Range Selector ---------------------------- -->
    <v-sheet color="grey lighten-4 pa-4 mb-4" rounded>
      <div class="d-flex align-center flex-row">
        <div class="flex-1 text-center">
          <div class="font-weight-bold text-h6">{{ startDate | humanDate }} <span class="grey--text text--lighten-1 text-h6 mx-2">-</span> {{ endDate | humanDate }}</div>
        </div>

        <v-btn icon color="grey" @click="resetDateRange" title="Reset to last 30 days"><v-icon>mdi-refresh</v-icon></v-btn>
        
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              icon 
              color="primary" 
              @click="openInteractiveAnalysis"
              v-bind="attrs"
              v-on="on"
              large
              class="mx-1"
            >
              <v-icon size="24">mdi-brain</v-icon>
            </v-btn>
          </template>
          <span>
            <strong>Interactive Analysis</strong><br>
            Analyze {{ totalCallsInRange || 'all' }} calls from {{ dateRange.start }} to {{ dateRange.end }}<br>
            Ask questions and get AI-powered insights
          </span>
        </v-tooltip>
        
         <v-btn 
           icon 
           :color="weeklyView ? 'primary' : 'grey'"
           @click="toggleWeek"
           :title="weeklyView ? 'Switch to Daily View' : 'Switch to Weekly View'"
         >
           <v-icon>{{ weeklyView ? 'mdi-calendar-week-outline' : 'mdi-calendar-outline' }}</v-icon>
         </v-btn>
        
        <!-- Date Picker Menu -->
        <v-menu
          v-model="datePickerMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-x
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn 
              icon 
              color="grey" 
              v-bind="attrs" 
              v-on="on"
              title="Select date range"
            >
              <v-icon>mdi-calendar-range</v-icon>
            </v-btn>
          </template>
          
          <v-date-picker
            v-model="selectedDateRange"
            range
            color="primary"
            :allowed-dates="isDateAllowed"
            :max="maxSelectableDate"
            :min="minSelectableDate"
            @change="onDateRangeChange"
          ></v-date-picker>
        </v-menu>
      </div>
    </v-sheet>

    <!-- Loading Indicator -->
    <v-progress-linear
      indeterminate
      color="primary"
      v-if="loading"
      class="mb-4"
    ></v-progress-linear>

    <!-- Error Message -->
    <v-alert
      v-if="errorMessage"
      type="warning"
      outlined
      dismissible
      @input="errorMessage = ''"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <!-- Data Status -->
    <v-alert
      v-if="dataStatus"
      :type="dataStatus.type"
      outlined
      class="mb-4"
    >
      {{ dataStatus.message }}
    </v-alert>

    <div v-if="!loading && trendingData">

      <!-- ====================== Call Volume ====================== -->
      <div class="d-flex flex-row align-center mb-6 mt-12">
        <div class="flex-1 d-flex flex-row align-center justify-center">
          <h1 class="d-inline-block">Call Volume</h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon class="ml-2" color="yellow darken-2" v-bind="attrs" v-on="on">
                mdi-information-outline
              </v-icon>
            </template>
            <span>Total number of calls per day</span>
          </v-tooltip>
        </div>
        <v-btn icon color="grey" @click="toggleVisibility('callVolume')"><v-icon>{{ visibility.callVolume ? "mdi-window-minimize" : "mdi-window-maximize" }}</v-icon></v-btn>
      </div>
      <v-sheet v-if="visibility.callVolume" color="grey lighten-4 pa-4 mb-4" rounded>
        <div v-if="trendingCharts.callVolume" class="text-center pa-4">
          <v-chart class="chart" :option="trendingCharts.callVolume" />
        </div>
        <div v-else class="text-center pa-4">
          No call volume data available for the selected date range.
        </div>
      </v-sheet>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Call Categories ====================== -->
      <div class="d-flex flex-row align-center mb-6">
        <div class="flex-1 d-flex flex-row align-center justify-center">
          <h1 class="d-inline-block">Call Categories</h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="ml-2"
                color="yellow darken-2"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline 
              </v-icon>
            </template>
            <span>Reason customers are calling</span>
          </v-tooltip>
        </div>
        <v-btn icon color="grey" @click="toggleVisibility('callCategories')"><v-icon>{{ visibility.callCategories ? "mdi-window-minimize" : "mdi-window-maximize" }}</v-icon></v-btn>
      </div>

      <v-sheet v-if="visibility.callCategories" color="grey lighten-4 pa-4 mb-4" rounded>
        <v-chart 
          class="chart" 
          :option="trendingCharts.callMainCategoriesByDate" 
          @click="(params) => handleChartClick(params, 'mainCategories')"
          ref="mainCategoriesChart"
        />
      </v-sheet>
              <v-sheet v-if="visibility.callCategories" color="grey lighten-4 pa-4 mb-4" rounded>
          <v-chart 
            class="chart" 
            :option="trendingCharts.callMainCategoriesByCategory" 
            @click="(params) => handleChartClick(params, 'mainCategoriesCategory')"
            ref="mainCategoriesCategoryChart"
          />
        </v-sheet>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Call Sub Categories ====================== -->
      <div class="d-flex flex-row align-center mb-6 mt-12">
        <div class="flex-1 d-flex flex-row align-center justify-center">
          <h1 class="d-inline-block">
            Call Sub Categories
            <span v-if="selectedMainCategory" class="text-h6 ml-2 primary--text">
              - {{ selectedMainCategory }}
            </span>
          </h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="ml-2"
                color="yellow darken-2"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>
              {{ selectedMainCategory ? 
                `Sub-categories for ${selectedMainCategory}. Click "Show All" to see all categories.` : 
                'Detailed breakdown of call reasons by sub category. Click on a main category above to filter.' 
              }}
            </span>
          </v-tooltip>
        </div>
        <v-btn 
          v-if="selectedMainCategory" 
          small 
          color="primary" 
          outlined 
          class="mr-2"
          @click="clearCategoryFilter"
        >
          <v-icon small class="mr-1">mdi-arrow-left</v-icon>
          Show All
        </v-btn>
        <v-btn icon color="grey" @click="toggleVisibility('callSubCategories')"><v-icon>{{ visibility.callSubCategories ? "mdi-window-minimize" : "mdi-window-maximize" }}</v-icon></v-btn>
      </div>
      <v-sheet v-if="visibility.callSubCategories" color="grey lighten-4 pa-4 mb-4" rounded>
        <v-chart 
          class="chart" 
          :option="trendingCharts.callSubCategoriesByDate" 
          @click="(params) => handleChartClick(params, 'subCategories')"
          ref="subCategoriesChart"
        />
      </v-sheet>
      <v-sheet v-if="visibility.callSubCategories" color="grey lighten-4 pa-4 mb-4" rounded>
        <v-chart 
          style="height: 800px;" 
          :option="trendingCharts.callSubCategoriesByCategory" 
          @click="(params) => handleChartClick(params, 'subCategoriesCategory')"
          ref="subCategoriesCategoryChart"
        />
      </v-sheet>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Customer Satisfaction ====================== -->
      <div class="d-flex flex-row align-center mb-6 mt-12">
        <div class="flex-1 d-flex flex-row align-center justify-center">
          <h1 class="d-inline-block">Customer Satisfaction</h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="ml-2"
                color="yellow darken-2"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>Customer satisfaction metrics and factors affecting satisfaction</span>
          </v-tooltip>
        </div>
        <v-btn icon color="grey" @click="toggleVisibility('customerSatisfaction')"><v-icon>{{ visibility.customerSatisfaction ? "mdi-window-minimize" : "mdi-window-maximize" }}</v-icon></v-btn>
      </div>
      <v-sheet v-if="visibility.customerSatisfaction" color="grey lighten-4 pa-4 mb-4" rounded>
        <v-chart 
          class="chart" 
          :option="trendingCharts.customerSatisfactionByDate" 
          @click="(params) => handleChartClick(params, 'satisfaction')"
          ref="satisfactionChart"
        />
      </v-sheet>
      <v-sheet v-if="visibility.customerSatisfaction" color="grey lighten-4 pa-4 mb-4" rounded>
        <v-chart 
          class="chart" 
          :option="trendingCharts.customerSatisfactionByCategory" 
          @click="(params) => handleChartClick(params, 'satisfactionCategory')"
          ref="satisfactionCategoryChart"
        />
      </v-sheet>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Sentiment Analysis ====================== -->
      <div class="d-flex flex-row align-center mb-6 mt-12">
        <div class="flex-1 d-flex flex-row align-center justify-center">
          <h1 class="d-inline-block">Sentiment Analysis</h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="ml-2"
                color="yellow darken-2"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>Call sentiment distribution and patterns across different categories</span>
          </v-tooltip>
        </div>
        <v-btn icon color="grey" @click="toggleVisibility('sentimentAnalysis')"><v-icon>{{ visibility.sentimentAnalysis ? "mdi-window-minimize" : "mdi-window-maximize" }}</v-icon></v-btn>
      </div>
      <v-sheet v-if="visibility.sentimentAnalysis" color="grey lighten-4 pa-4 mb-4" rounded>
        <h3 class="mb-4">Sentiment Buckets by Date</h3>
        <v-chart 
          class="chart" 
          :option="trendingCharts.sentimentByDate" 
          @click="(params) => handleChartClick(params, 'sentiment')"
          ref="sentimentChart"
        />
      </v-sheet>
      <v-sheet v-if="visibility.sentimentAnalysis" color="grey lighten-4 pa-4 mb-4" rounded>
        <h3 class="mb-4">Sentiment Buckets by Category</h3>
        <v-chart 
          class="chart" 
          :option="trendingCharts.sentimentByCategory" 
          @click="(params) => handleChartClick(params, 'sentimentCategory')"
          ref="sentimentCategoryChart"
        />
      </v-sheet>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Sentiment Trajectories ====================== -->
      <div class="d-flex flex-row align-center mb-6 mt-12">
        <div class="flex-1 d-flex flex-row align-center justify-center">
          <h1 class="d-inline-block">Sentiment Trajectories</h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="ml-2"
                color="yellow darken-2"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>Sentiment patterns and trajectories throughout calls</span>
          </v-tooltip>
        </div>
        <v-btn icon color="grey" @click="toggleVisibility('sentimentTrajectories')"><v-icon>{{ visibility.sentimentTrajectories ? "mdi-window-minimize" : "mdi-window-maximize" }}</v-icon></v-btn>
      </div>
      <v-sheet v-if="visibility.sentimentTrajectories" color="grey lighten-4 pa-4 mb-4" rounded>
        <h3 class="mb-4">Sentiment Trajectories by Date</h3>
        <v-chart 
          class="chart" 
          :option="trendingCharts.sentimentTrajectoriesByDate" 
          @click="(params) => handleChartClick(params, 'sentimentTrajectories')"
          ref="sentimentTrajectoriesChart"
        />
      </v-sheet>
      <v-sheet v-if="visibility.sentimentTrajectories" color="grey lighten-4 pa-4 mb-4" rounded>
        <h3 class="mb-4">Sentiment Trajectories by Category</h3>
        <v-chart 
          class="chart" 
          :option="trendingCharts.sentimentTrajectoriesByCategory" 
          @click="(params) => handleChartClick(params, 'sentimentTrajectoriesCategory')"
          ref="sentimentTrajectoriesCategoryChart"
        />
      </v-sheet>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Call Duration ====================== -->
      <div class="d-flex flex-row align-center mb-6 mt-12">
        <div class="flex-1 d-flex flex-row align-center justify-center">
          <h1 class="d-inline-block">Call Duration</h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="ml-2"
                color="yellow darken-2"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>Distribution of call lengths in minute ranges</span>
          </v-tooltip>
        </div>
        <v-btn icon color="grey" @click="toggleVisibility('callDuration')"><v-icon>{{ visibility.callDuration ? "mdi-window-minimize" : "mdi-window-maximize" }}</v-icon></v-btn>
      </div>
      <v-sheet v-if="visibility.callDuration" color="grey lighten-4 pa-4 mb-4" rounded>
        <h3 class="mb-4">Call Duration by Date</h3>
        <v-chart 
          class="chart" 
          :option="trendingCharts.callDurationByDate" 
          @click="(params) => handleChartClick(params, 'callDuration')"
          ref="callDurationChart"
        />
      </v-sheet>
      <v-sheet v-if="visibility.callDuration" color="grey lighten-4 pa-4 mb-4" rounded>
        <h3 class="mb-4">Call Duration by Category</h3>
        <v-chart 
          class="chart" 
          :option="trendingCharts.callDurationByCategory" 
          @click="(params) => handleChartClick(params, 'callDurationCategory')"
          ref="callDurationCategoryChart"
        />
      </v-sheet>

      <v-divider class="mx-12 my-6"></v-divider>

      <!-- ====================== Repeat Callers ====================== -->
      <!-- cs_call_analysis_trends.repeat_callers -->
      <div class="d-flex flex-row align-center mb-6 mt-12">
        <div class="flex-1 d-flex flex-row align-center justify-center">
          <h1 class="d-inline-block">Repeat Callers</h1>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                class="ml-2"
                color="yellow darken-2"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>Number of callers with more than one call over the last month.</span>
          </v-tooltip>
        </div>
        <v-btn icon color="grey" @click="toggleVisibility('repeatCallers')"><v-icon>{{ visibility.repeatCallers ? "mdi-window-minimize" : "mdi-window-maximize" }}</v-icon></v-btn>
      </div>
      <v-sheet v-if="visibility.repeatCallers" color="grey lighten-4 pa-4 mb-4" rounded>
        <div v-if="trendingCharts.repeatCallers" class="text-center pa-4">
          <v-chart 
            style="height: 800px;" 
            :option="trendingCharts.repeatCallers" 
            @click="(params) => handleChartClick(params, 'repeatCallers')"
            ref="repeatCallersChart"
          />
        </div>
        <div v-else class="text-center pa-4">
          No repeat callers data available for the selected date range.
        </div>
      </v-sheet>
      <!-- <v-card v-if="repeatCallersTable">
        <v-card-title>
          {{ repeatCallersTable.name | phoneNumber }}
          <v-spacer></v-spacer>
          <v-btn icon @click="repeatCallersTable = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="d-flex flex-wrap justify-center">
          <v-btn small class="ma-1" v-for="(callId, callIndex) in repeatCallersTable.value" :key="'repeat-call-' + callIndex" @click="openCall(callId)">
            {{ callId }}
          </v-btn>
        </v-card-text>
      </v-card> -->



      <v-divider class="mx-12 my-6"></v-divider>

    </div>

    <CallAnalysisCallDialog v-model="callRecordingDetailsDialog" :callId="callId" />

    <!-- Calls Dialog -->
    <v-dialog v-model="callsDialog.show" max-width="800px" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          {{ callsDialog.title }}
          <v-spacer></v-spacer>
          <v-chip color="primary" small>{{ callsDialog.calls.length }} calls</v-chip>
          <v-btn icon @click="closeCallsDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 500px;">
          <div v-if="callsDialog.calls.length === 0" class="text-center pa-4 grey--text">
            No calls found for this selection.
          </div>
          <!-- Special handling for informational messages (like repeat caller limitations) -->
          <div v-else-if="isInformationalDialog" class="pa-4">
            <div v-for="(message, index) in callsDialog.calls" :key="'info-' + index" class="mb-3">
              <v-alert 
                v-if="message.includes('📞')" 
                type="info" 
                outlined 
                class="mb-2"
              >
                {{ message }}
              </v-alert>
              <v-alert 
                v-else-if="message.includes('⚠️')" 
                type="warning" 
                outlined 
                class="mb-2"
              >
                {{ message }}
              </v-alert>
              <div v-else class="text-body-1 mb-2">
                {{ message }}
              </div>
            </div>
          </div>
          <!-- Normal call buttons -->
          <div v-else class="d-flex flex-wrap">
             <v-btn 
               small 
               class="ma-1" 
               color="primary"
               outlined
               v-for="(callId, index) in callsDialog.calls" 
               :key="'dialog-call-' + index"
               @click="openCall(callId)"
             >
               {{ callId }}
             </v-btn>
           </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="!isInformationalDialog && callsDialog.calls.length > 0"
            color="primary"
            @click="exportToInteractiveTool"
            outlined
            class="mr-1"
            :loading="isExportingToInteractive"
            :disabled="isExportingToInteractive"
          >
            <template v-slot:loader>
              <v-progress-circular 
                indeterminate 
                color="primary" 
                size="20" 
                width="2"
              ></v-progress-circular>
            </template>
            <v-icon left small>mdi-brain</v-icon>
            Interactive Tool
          </v-btn>

          <v-btn
            v-if="!isInformationalDialog && callsDialog.calls.length > 0"
            color="secondary"
            @click="exportToClipboard"
            text
            class="mr-1"
          >
            <v-icon left small>mdi-content-copy</v-icon>
            Copy to Clipboard
          </v-btn>

          <v-btn
            v-if="!isInformationalDialog && callsDialog.calls.length > 0"
            color="success"
            @click="exportToGoogleSheets"
            text
            class="mr-1"
            :loading="exportingToSheets"
          >
            <v-icon left small>mdi-google-spreadsheet</v-icon>
            Export to Sheets
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="closeCallsDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Toast Notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
      top
      right
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

  </v-card-text>
</template>

<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, LineChart, BarChart, RadarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
} from 'echarts/components'
import VChart from 'vue-echarts'
import axios from 'axios'; // Import axios
import { DateTime } from 'luxon'
import { getFirestore, collection, getDocs, query as firestoreQuery, where, orderBy, limit, doc, getDoc, startAt, endAt } from 'firebase/firestore';
import CallAnalysisCallDialog from '@/components/CustomerService/CallAnalysisCallDialog.vue';
// Register ECharts components
use([
  CanvasRenderer,
  PieChart,
  LineChart,
  BarChart,
  RadarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent
])

export default {
  name: 'CallAnalysisTrends',
  components: {
    VChart,
    CallAnalysisCallDialog,
  },
  data() {
    return {
      // Google Sheets Export Configuration
      // Set this to your preferred Google Drive folder ID where exported sheets should be saved
      // Leave as null to use the default folder
      exportFolderId: '16N2siPRwre3WwRleD8OQVQ857z2hj7tx',
      
      // Fields to export to Google Sheets (leave null to use defaults)
      // Available fields include: id, date, time, duration, phone_number, agent, 
      // categorization.main, categorization.sub, sentiment_analysis.overall_sentiment_score,
      // satisfaction_score.total, summary.brief, summary.short, summary.detailed, 
      // opportunities, heuristic_hold_time, status
      exportFields: [
        'id',
        'date', 
        'time',
        'duration',
        'phone_number',
        'agent',
        'categorization.main',
        'categorization.sub',
        'sentiment_analysis.overall_sentiment_score',
        'satisfaction_score.total',
        'summary.brief'
      ],
      
      lastVueClickTime: 0, // Track when Vue @click handlers last fired
      loading: false,
      startDate: null,
      endDate: null,
      dateRange: {
        start: null,
        end: null
      },
      // Category mapping for drill-down functionality
      categoryMapping: {
        'Billing and Payments': [
          'Account Balance & Bill Inquiries',
          'High Bill / Usage Inquiries', 
          'Other Billing disputes',
          'Budget billing and equal payment plans',
          'Payment methods and options',
          'Payment issues',
          'Late payments, and collections',
          'Payment arrangements and extensions', 
          'Payment Confirmation',
          'Pre-authorized payments',
          'Financial Assistance & Subsidies',
          'Security deposits (questions, refunds)'
        ],
        'Rates and Plans': [
          'Rate plan options and comparisons',
          'Understanding energy Usage & Consumption',
          'Energy efficiency programs and rebates'
        ],
        'Account Management': [
          'Updating account information',
          'New account setup',
          'Account closure and termination',
          'Moves - service start/stop',
          'Merging/linking multiple accounts', 
          'Deceased Account Management',
          'Landlord and tenants',
          'MyAccount Technical Support'
        ],
        'Service and Connections': [
          'Service Requests',
          'Vegetation Management'
        ],
        'Outages and Emergencies': [
          'Power outages',
          'Power Quality & Reliability',
          'Safety concerns during outages',
          'Generator inquiries and safety',
          'Troubleshooting electrical issues',
          'Battery Loan',
          'Outage communicatons'
        ],
        'Program and Service Support': [
          'Distributed Energy Resources (DER)'
        ],
        'Other Inquiries': [
          'Privacy policy and data protection',
          'Feedback and suggestions',
          'Scam / Fraud',
          'Other'
        ]
      },
      selectedMainCategory: null, // Track which main category is selected for drill-down
      // Color palette for main categories and their sub-category shades
      categoryColors: {
        'Account Management': '#5470c6',      // Blue
        'Billing and Payments': '#22c55e',    // Green  
        'Service and Connections': '#f59e0b', // Orange
        'Rates and Plans': '#8b5cf6',         // Purple
        'Outages and Emergencies': '#ef4444', // Red (as requested)
        'Program and Service Support': '#06b6d4', // Cyan
        'Other Inquiries': '#6b7280'          // Gray
      },
      trendingData: {
        callVolume: [],
        callMainCategoriesByDate: [],
        callMainCategoriesByCategory: [],
        callSubCategoriesByDate: [],
        callSubCategoriesByCategory: [],

        repeatCallers: {},
        repeatCallersProcessed: [],
        customerEffort: [],
        escalations: [],
        longCalls: [],
        sentimentScores: []
      },
      trendingCharts: {
        callVolume: null,
        callMainCategoriesByDate: null,
        callMainCategoriesByCategory: null,
        callSubCategoriesByDate: null,
        callSubCategoriesByCategory: null,

        repeatCallers: null,
        customerEffort: null,
        escalations: null,
        longCalls: null,
        sentimentScores: null,
      },
      insightIndex: 0,
      callId: "",
      callRecordingDetailsDialog: false,
      errorMessage: '',
      dataStatus: null,
      availableDateRange: {
        start: null,
        end: null
      },
      availableDatesSet: new Set(), // Store available dates for fast lookup
      dateValidationMessage: '',
      validatingDates: false,
      selectedDateRange: null,
      maxSelectableDate: null,
      minSelectableDate: null,
      isValidSelectedRange: true,
      datePickerMenu: false,
      visibility: {
        callVolume: true,
        callCategories: true,
        callSubCategories: true,
        customerSatisfaction: true,
        sentimentAnalysis: true,
        sentimentTrajectories: true,
        callDuration: true,
        repeatCallers: false,
      },
      // Dialog for showing calls
      callsDialog: {
        show: false,
        title: '',
        calls: [],
        category: '',
        date: '',
        chartType: ''
      },
      // Toast notifications
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      // Weekly view toggle
      weeklyView: true,
      // Export loading state
      exportingToSheets: false,
      exportingToInteractive: false,
    }
  },
  methods: {
     toggleVisibility(section) {
       this.visibility[section] = !this.visibility[section];
     },
     
          handleChartClick(params, chartType = 'mainCategories') {
       console.log('handleChartClick called:', params, 'Chart type:', chartType);
       
       // Track that Vue @click handler fired
       this.lastVueClickTime = Date.now();
       
       // Add specific debugging for sub categories
       if (chartType.includes('subCategories')) {
         console.log('Sub categories click details:', {
           componentType: params.componentType,
           seriesName: params.seriesName,
           name: params.name,
           dataIndex: params.dataIndex,
           value: params.value
         });
       }
       
       // Handle different click scenarios
       if (params.componentType === 'series') {
         // Clicked on a data series (bar)
         const { seriesName, name, value, dataIndex, seriesIndex, stackedData, isGridClick } = params;
         
         console.log('Clicked data details:', {
           category: seriesName,
           date: name,
           value: value,
           dataIndex: dataIndex,
           seriesIndex: seriesIndex,
           chartType: chartType,
           isGridClick: isGridClick,
           hasStackedData: !!stackedData
         });
         
         // Handle aggregated stacked data (from grid clicks)
         if (stackedData && stackedData.length > 0) {
           console.log('Handling stacked data:', stackedData);
           this.showStackedDataDialog(name, stackedData, chartType);
           return;
         }
         
         // For horizontal bar charts, the category name is in 'name', not 'seriesName'
         let actualCategoryName = seriesName;
         let actualDateName = name;
         
         // Check if this is a horizontal bar chart (category charts or repeat callers)
         const isHorizontalBarChart = chartType.includes('Category') || chartType === 'repeatCallers';
         
         if (isHorizontalBarChart) {
           // For horizontal bar charts, the category is the 'name' parameter
           actualCategoryName = name;
           actualDateName = name; // For category charts, there's no separate date
           
           // Special handling for call duration charts - strip " min" suffix if present
           if (chartType.includes('callDuration') && actualCategoryName.endsWith(' min')) {
             actualCategoryName = actualCategoryName.replace(' min', '');
             console.log('Call duration chart - stripped " min" suffix. New category name:', actualCategoryName);
           }
           
           console.log('Horizontal bar chart detected. Chart type:', chartType, 'Using category name:', actualCategoryName);
         } else {
           // For vertical stacked charts, handle series name formatting
           if (chartType === 'callDuration' && actualCategoryName.endsWith(' min')) {
             actualCategoryName = actualCategoryName.replace(' min', '');
             console.log('Call duration stacked chart - stripped " min" suffix from series name. New category name:', actualCategoryName);
           }
         }
         
         // Get the appropriate data based on chart type
         let dateData = null;
         
         if (chartType === 'mainCategories') {
           dateData = this.trendingData.callMainCategoriesByDate[dataIndex];
         } else if (chartType === 'subCategories') {
           dateData = this.trendingData.callSubCategoriesByDate[dataIndex];
         } else if (chartType === 'satisfaction') {
           dateData = this.trendingData.customerSatisfactionByDate[dataIndex];
         } else if (chartType === 'sentiment') {
           dateData = this.trendingData.sentimentByDate[dataIndex];
         } else if (chartType === 'sentimentTrajectories') {
           dateData = this.trendingData.sentimentTrajectoriesByDate[dataIndex];
         } else if (chartType === 'callDuration') {
           dateData = this.trendingData.callDurationByDate[dataIndex];
         } else if (chartType.includes('Category') || chartType === 'repeatCallers') {
           // For horizontal bar charts, we don't need dateData 
           // The showCallsForDataPoint method will handle category charts directly
           dateData = null;
         }
         
         console.log('Full date data:', dateData);
         
         // Show the calls for this data point
         this.showCallsForDataPoint(actualCategoryName, actualDateName, dateData, chartType, dataIndex);
         
       } else if (params.componentType === 'xAxis') {
         // Clicked on x-axis label
         console.log('Clicked on x-axis:', params.value);
       } else if (params.componentType === 'yAxis') {
         // Clicked on y-axis
         console.log('Clicked on y-axis:', params.value);
       } else {
         // Clicked on other chart elements
         console.log('Clicked on chart element:', params.componentType);
       }
     },
     
          setupAdvancedClickDetection() {
       // Set up advanced click detection for ALL charts
       this.$nextTick(() => {
         // Get all chart refs and set up grid click detection
         const chartConfigs = [
           { ref: 'mainCategoriesChart', type: 'mainCategories', orientation: 'vertical' },
           { ref: 'mainCategoriesCategoryChart', type: 'mainCategoriesCategory', orientation: 'horizontal' },
           { ref: 'subCategoriesChart', type: 'subCategories', orientation: 'vertical' },
           { ref: 'subCategoriesCategoryChart', type: 'subCategoriesCategory', orientation: 'horizontal' },
           { ref: 'satisfactionChart', type: 'satisfaction', orientation: 'vertical' },
           { ref: 'satisfactionCategoryChart', type: 'satisfactionCategory', orientation: 'horizontal' },
           { ref: 'sentimentChart', type: 'sentiment', orientation: 'vertical' },
           { ref: 'sentimentCategoryChart', type: 'sentimentCategory', orientation: 'horizontal' },
           { ref: 'sentimentTrajectoriesChart', type: 'sentimentTrajectories', orientation: 'vertical' },
           { ref: 'sentimentTrajectoriesCategoryChart', type: 'sentimentTrajectoriesCategory', orientation: 'horizontal' },
           { ref: 'callDurationChart', type: 'callDuration', orientation: 'vertical' },
           { ref: 'callDurationCategoryChart', type: 'callDurationCategory', orientation: 'horizontal' },
           { ref: 'repeatCallersChart', type: 'repeatCallers', orientation: 'horizontal' }
         ];
         
         chartConfigs.forEach(config => {
           this.setupChartGridClickDetection(config.ref, config.type, config.orientation);
         });
       });
     },
     
     setupChartGridClickDetection(refName, chartType, orientation) {
       const chartRef = this.$refs[refName];
       if (chartRef && chartRef.chart) {
         const chart = chartRef.chart;
         
         let clickTimeout = null;
         
         // Add click detection for the entire grid area
         chart.getZr().on('click', (params) => {
           console.log('Raw click detected on chart:', chartType, 'at pixel:', [params.offsetX, params.offsetY]);
           
           // Clear any pending timeout
           if (clickTimeout) {
             clearTimeout(clickTimeout);
           }
           
           // Always process grid clicks after a short delay to allow Vue @click to fire first
           clickTimeout = setTimeout(() => {
             // Check if Vue @click handler fired recently (within last 150ms)
             const timeSinceVueClick = Date.now() - this.lastVueClickTime;
             
             if (timeSinceVueClick < 150) {
               console.log('Vue @click handler fired recently, skipping grid click for:', chartType);
               return;
             }
             
             console.log('Processing grid click for:', chartType, '(no recent Vue click)');
             
             const pointInPixel = [params.offsetX, params.offsetY];
             
             // Check if the click is within the grid area
             if (chart.containPixel('grid', pointInPixel)) {
               const pointInGrid = chart.convertFromPixel('grid', pointInPixel);
               this.handleGridAreaClick(pointInGrid, chartType, orientation, chart);
             } else {
               console.log('Click is outside grid area, ignoring');
             }
           }, 100); // Longer delay to ensure Vue @click has time to fire if it's going to
         });
       }
     },
     
     handleGridAreaClick(pointInGrid, chartType, orientation, chart) {
       console.log('Grid area click detected:', pointInGrid, chartType, orientation);
       
       const option = chart.getOption();
       
       if (orientation === 'horizontal') {
         // Horizontal bar charts - click anywhere in the row for that category
         const yIndex = Math.floor(pointInGrid[1]);
         const categories = option.yAxis[0].data;
         
         if (yIndex >= 0 && yIndex < categories.length) {
           const clickedCategory = categories[yIndex];
           console.log('Clicked in row for category:', clickedCategory);
           
           // Get the value for this category
           const value = this.getValueForCategory(clickedCategory, chartType);
           
           // Simulate a series click event
           this.handleChartClick({
             componentType: 'series',
             seriesName: clickedCategory,
             name: clickedCategory,
             dataIndex: yIndex,
             seriesIndex: 0,
             value: value
           }, chartType);
         }
       } else {
         // Vertical bar charts - click anywhere in the column for that date
         const xIndex = Math.floor(pointInGrid[0]);
         const dates = option.xAxis[0].data;
         
         if (xIndex >= 0 && xIndex < dates.length) {
           const clickedDate = dates[xIndex];
           console.log('Clicked in column for date:', clickedDate);
           
           // Check if this is a stacked chart
           const isStackedChart = option.series && option.series.length > 1;
           
           if (isStackedChart) {
             console.log('Stacked chart detected - showing all data for date:', clickedDate);
             
             // For stacked charts, always show all data for the date
             // Get aggregated data for this date from all series
             const aggregatedData = this.getAggregatedDataForDate(xIndex, chartType);
             
             if (aggregatedData && aggregatedData.totalCalls > 0) {
               // Show the aggregated data
               this.handleChartClick({
                 componentType: 'series',
                 seriesName: 'All Categories',
                 name: clickedDate,
                 dataIndex: xIndex,
                 seriesIndex: -1, // Special indicator for aggregated data
                 value: aggregatedData.totalCalls,
                 stackedData: aggregatedData.seriesBreakdown,
                 isGridClick: true
               }, chartType);
             } else {
               // Fallback to date details if no aggregated data
               const isoDate = this.convertHumanDateToISO(clickedDate);
               this.showDateDetails(isoDate);
             }
           } else {
             // Single series chart - get the value at this position
             const series = option.series[0];
             const value = series && series.data && series.data[xIndex] || 0;
             
             this.handleChartClick({
               componentType: 'series',
               seriesName: series ? series.name : 'Data',
               name: clickedDate,
               dataIndex: xIndex,
               seriesIndex: 0,
               value: value,
               isGridClick: true
             }, chartType);
           }
         }
       }
     },
     
     getValueForCategory(category, chartType) {
       // Get the value for a category from the appropriate data structure
       const dataMap = {
         'mainCategoriesCategory': this.trendingData.callMainCategoriesByCategory,
         'subCategoriesCategory': this.trendingData.callSubCategoriesByCategory,
         'satisfactionCategory': this.trendingData.customerSatisfactionByCategory,
         'sentimentCategory': this.trendingData.sentimentByCategory,
         'sentimentTrajectoriesCategory': this.trendingData.sentimentTrajectoriesByCategory,
         'callDurationCategory': this.trendingData.callDurationByCategory,
         'repeatCallers': null // Special case, will handle separately
       };
       
       if (chartType === 'repeatCallers') {
         // For repeat callers, find the call count from processed data
         const callerData = this.trendingData.repeatCallersProcessed?.find(([phone, count]) => 
           this.formatPhoneNumber(phone) === category
         );
         return callerData ? callerData[1] : 0;
       }
       
       const data = dataMap[chartType];
       return data && data[category] ? data[category].count : 0;
     },
     
     getAggregatedDataForDate(xIndex, chartType) {
       // Get aggregated data for all series at a specific date index
       const dataMap = {
         'mainCategories': this.weeklyView ? this.getDisplayData(this.trendingData.callMainCategoriesByDate) : this.trendingData.callMainCategoriesByDate,
         'subCategories': this.weeklyView ? this.getDisplayData(this.trendingData.callSubCategoriesByDate) : this.trendingData.callSubCategoriesByDate,
         'satisfaction': this.weeklyView ? this.getDisplayData(this.trendingData.customerSatisfactionByDate) : this.trendingData.customerSatisfactionByDate,
         'sentiment': this.weeklyView ? this.getDisplayData(this.trendingData.sentimentByDate) : this.trendingData.sentimentByDate,
         'sentimentTrajectories': this.weeklyView ? this.getDisplayData(this.trendingData.sentimentTrajectoriesByDate) : this.trendingData.sentimentTrajectoriesByDate,
         'callDuration': this.weeklyView ? this.getDisplayData(this.trendingData.callDurationByDate) : this.trendingData.callDurationByDate
       };
       
       const dateData = dataMap[chartType];
       if (!dateData || !dateData[xIndex]) {
         console.log('No date data found for index:', xIndex, 'in', chartType);
         return null;
       }
       
       const dayData = dateData[xIndex].value;
       if (!dayData) {
         console.log('No day data found for index:', xIndex);
         return null;
       }
       
       const seriesBreakdown = [];
       let totalCalls = 0;
       
       // Aggregate all categories/series for this date/week
       Object.keys(dayData).forEach(key => {
         const categoryData = dayData[key];
         let calls = 0;
         
         // Different data structures have calls in different places
         if (categoryData) {
           if (categoryData.calls && Array.isArray(categoryData.calls)) {
             calls = categoryData.calls.length;
           } else if (categoryData.total !== undefined) {
             calls = categoryData.total;
           } else if (typeof categoryData === 'number') {
             calls = categoryData;
           }
         }
         
         if (calls > 0) {
           seriesBreakdown.push({
             name: key,
             count: calls,
             calls: categoryData.calls || []
           });
           totalCalls += calls;
         }
       });
       
       console.log('Aggregated data for date index', xIndex, ':', {
         totalCalls,
         seriesBreakdown
       });
       
       return {
         totalCalls,
         seriesBreakdown
       };
     },
     
     findSeriesAtPosition(yValue, xIndex, chartType, chart) {
       // Get the series data for this x-axis position
       const seriesData = this.getSeriesDataForPosition(xIndex, chartType);
       if (!seriesData || seriesData.length === 0) return null;
       
       // For stacked charts, find which series the Y coordinate falls into
       let cumulativeValue = 0;
       
       for (let i = 0; i < seriesData.length; i++) {
         const series = seriesData[i];
         const nextCumulative = cumulativeValue + series.value;
         
         if (yValue >= cumulativeValue && yValue <= nextCumulative && series.value > 0) {
           return {
             name: series.name,
             value: series.value,
             index: i
           };
         }
         cumulativeValue = nextCumulative;
       }
       
       // If no exact match found, return the largest series for this position
       const largestSeries = seriesData.reduce((max, current) => 
         current.value > max.value ? current : max, seriesData[0]
       );
       
       return largestSeries && largestSeries.value > 0 ? {
         name: largestSeries.name,
         value: largestSeries.value,
         index: seriesData.indexOf(largestSeries)
       } : null;
     },
     
     getSeriesDataForPosition(xIndex, chartType) {
       // Get all series data for a specific x-axis position
       const dataMap = {
         'mainCategories': this.trendingData.callMainCategoriesByDate,
         'subCategories': this.trendingData.callSubCategoriesByDate,
         'satisfaction': this.trendingData.customerSatisfactionByDate,
         'sentiment': this.trendingData.sentimentByDate,
         'sentimentTrajectories': this.trendingData.sentimentTrajectoriesByDate,
         'callDuration': this.trendingData.callDurationByDate
       };
       
       const dateData = dataMap[chartType];
       if (!dateData || !dateData[xIndex]) return [];
       
       const dayData = dateData[xIndex].value;
       const seriesData = [];
       
       Object.keys(dayData || {}).forEach((key, index) => {
         const value = dayData[key]?.total || dayData[key]?.calls?.length || 0;
         seriesData.push({
           name: key,
           value: value,
           index: index
         });
       });
       
       return seriesData;
     },
     
     getTotalStackHeightAtPosition(xIndex, chartType) {
       // Calculate the total height of all stacked bars at a given x position
       const seriesData = this.getSeriesDataForPosition(xIndex, chartType);
       if (!seriesData || seriesData.length === 0) return 0;
       
       // Sum up all the values to get total stack height
       const totalHeight = seriesData.reduce((sum, series) => sum + (series.value || 0), 0);
       
       console.log('Series data at position', xIndex, ':', seriesData);
       console.log('Total calculated height:', totalHeight);
       
       return totalHeight;
     },
     
     showStackedDataDialog(dateName, stackedData, chartType) {
       console.log('Showing stacked data dialog for:', dateName, stackedData);
       
       // Aggregate all calls from all categories
       let allCalls = [];
       let totalCalls = 0;
       
       stackedData.forEach(category => {
         if (category.calls && Array.isArray(category.calls)) {
           allCalls.push(...category.calls);
         }
         totalCalls += category.count || 0;
       });
       
       // Format the title
       const formattedDate = this.formatDateForTitle(dateName);
       const title = `All calls on ${formattedDate} (${totalCalls} total)`;
       
       // Set the dialog properties to show it
       this.callsDialog = {
         show: true,
         title: title,
         calls: [...new Set(allCalls)], // Remove duplicates
         category: 'All Categories',
         date: dateName,
         chartType: chartType
       };
     },
     
     convertHumanDateToISO(humanDate) {
       // Convert "January 15, 2025" back to "2025-01-15" format
       try {
         const parsed = DateTime.fromFormat(humanDate, 'MMMM d, yyyy');
         return parsed.toFormat('yyyy-MM-dd');
       } catch (e) {
         console.warn('Could not parse date:', humanDate);
         return humanDate; // Return as-is if parsing fails
       }
     },
     
     showCallsForDataPoint(seriesName, dateName, dateData, chartType, dataIndex = null) {
       console.log('Showing calls for:', { seriesName, dateName, dateData, chartType, dataIndex });
       
       // Enhanced debugging for problematic charts
       if (chartType === 'subCategoriesCategory') {
         console.log('Sub categories category chart - Available data:', Object.keys(this.trendingData.callSubCategoriesByCategory || {}));
         console.log('Looking for series:', seriesName);
         console.log('Data for this series:', this.trendingData.callSubCategoriesByCategory[seriesName]);
       } else if (chartType === 'callDuration') {
         console.log('Call duration by date chart - Available data:', this.trendingData.callDurationByDate?.length, 'dates');
         console.log('Looking for date index:', dataIndex, 'series:', seriesName);
         console.log('Date data:', dateData);
       } else if (chartType === 'callDurationCategory') {
         console.log('Call duration category chart - Available data:', Object.keys(this.trendingData.callDurationByCategory || {}));
         console.log('Looking for series:', seriesName);
         console.log('Data for this series:', this.trendingData.callDurationByCategory[seriesName]);
       } else if (chartType === 'repeatCallers') {
         console.log('Repeat callers chart - Available data:', this.trendingData.repeatCallersProcessed?.length, 'callers');
         console.log('Looking for series:', seriesName);
       }
       
       let calls = [];
       let title = '';
       
       // Extract calls based on chart type and series
       if (chartType === 'mainCategories' && dateData && dateData.value) {
         const categoryData = dateData.value[seriesName];
         if (categoryData && categoryData.calls) {
           calls = categoryData.calls;
           title = `${seriesName} calls on ${this.formatDateForTitle(dateName)}`;
         }
       } else if (chartType === 'subCategories' && dateData && dateData.value) {
         const subCategoryData = dateData.value[seriesName];
         if (subCategoryData && subCategoryData.calls) {
           calls = subCategoryData.calls;
           title = `${seriesName} calls on ${this.formatDateForTitle(dateName)}`;
         }
       } else if (chartType === 'satisfaction' && dateData && dateData.value) {
         const satisfactionData = dateData.value[seriesName];
         if (satisfactionData && satisfactionData.calls) {
           calls = satisfactionData.calls;
           title = `${seriesName} on ${this.formatDateForTitle(dateName)}`;
         }
       } else if (chartType === 'sentiment' && dateData && dateData.value) {
         const sentimentData = dateData.value[seriesName];
         if (sentimentData && sentimentData.calls) {
           calls = sentimentData.calls;
           title = `${seriesName} sentiment on ${this.formatDateForTitle(dateName)}`;
         }
       } else if (chartType === 'sentimentTrajectories' && dateData && dateData.value) {
         const trajectoryData = dateData.value[seriesName];
         if (trajectoryData && trajectoryData.calls) {
           calls = trajectoryData.calls;
           title = `${seriesName} trajectory on ${this.formatDateForTitle(dateName)}`;
         }
       } else if (chartType === 'callDuration' && dateData && dateData.value) {
         const durationData = dateData.value[seriesName];
         if (durationData && durationData.calls) {
           calls = durationData.calls;
           title = `${seriesName} minute calls on ${this.formatDateForTitle(dateName)}`;
         }
       }
       
               // For category-based charts (horizontal bar charts)
        if (chartType.includes('Category') && this.trendingData) {
          if (chartType === 'mainCategoriesCategory') {
            // For main categories, implement drill-down instead of showing calls
            this.drillDownToSubCategories(seriesName);
            return; // Exit early, don't show calls dialog
          } else if (chartType === 'subCategoriesCategory') {
            const categoryData = this.trendingData.callSubCategoriesByCategory;
            if (categoryData && categoryData[seriesName] && categoryData[seriesName].calls) {
              calls = categoryData[seriesName].calls;
              title = `All ${seriesName} calls`;
            }
          } else if (chartType === 'satisfactionCategory') {
            const categoryData = this.trendingData.customerSatisfactionByCategory;
            if (categoryData && categoryData[seriesName] && categoryData[seriesName].calls) {
              calls = categoryData[seriesName].calls;
              title = `All ${seriesName} calls`;
            }
          } else if (chartType === 'sentimentCategory') {
            const categoryData = this.trendingData.sentimentByCategory;
            if (categoryData && categoryData[seriesName] && categoryData[seriesName].calls) {
              calls = categoryData[seriesName].calls;
              title = `All ${seriesName} sentiment calls`;
            }
          } else if (chartType === 'sentimentTrajectoriesCategory') {
            const categoryData = this.trendingData.sentimentTrajectoriesByCategory;
            if (categoryData && categoryData[seriesName] && categoryData[seriesName].calls) {
              calls = categoryData[seriesName].calls;
              title = `All ${seriesName} trajectory calls`;
            }
          } else if (chartType === 'callDurationCategory') {
            const categoryData = this.trendingData.callDurationByCategory;
            if (categoryData && categoryData[seriesName] && categoryData[seriesName].calls) {
              calls = categoryData[seriesName].calls;
              title = `All ${seriesName} minute calls`;
            }
          }
        }
        
        // For repeat callers chart
        if (chartType === 'repeatCallers' && this.trendingData.repeatCallersProcessed) {
          // Find the phone number and its call count
          const phoneNumber = seriesName; // The phone number is the series name
          const callerData = this.trendingData.repeatCallersProcessed.find(([phone, count]) => 
            this.formatPhoneNumber(phone) === phoneNumber
          );
          if (callerData) {
            // Show loading message first
            this.callsDialog = {
              show: true,
              title: `${phoneNumber} - Searching...`,
              calls: ['🔍 Searching for calls...', 'Please wait while we query the database.'],
              category: seriesName,
              date: dateName,
              chartType: chartType
            };
            
            // Search through call database asynchronously
            this.findCallsByPhoneNumber(phoneNumber).then(callsForPhoneNumber => {
              // Update dialog with results
              this.callsDialog = {
                show: true,
                title: `${phoneNumber} (${Array.isArray(callsForPhoneNumber) ? callsForPhoneNumber.length : 0} calls found)`,
                calls: callsForPhoneNumber,
                category: seriesName,
                date: dateName,
                chartType: chartType
              };
            }).catch(error => {
              console.error('Error finding calls:', error);
              this.callsDialog = {
                show: true,
                title: `${phoneNumber} - Error`,
                calls: ['⚠️ Error searching for calls', error.message || 'Unknown error occurred'],
                category: seriesName,
                date: dateName,
                chartType: chartType
              };
            });
            
            // Return early since we're handling this asynchronously
            return;
          }
        }
       
       // Only show the dialog if we have calls or a meaningful title
       if (calls.length > 0 || title.includes('(')) {
         this.callsDialog = {
           show: true,
           title: title || `No calls found for ${seriesName}`,
           calls: calls,
           category: seriesName,
           date: dateName,
           chartType: chartType
         };
       } else {
         console.warn('No calls found for:', { seriesName, dateName, chartType });
         console.warn('Available data keys:', Object.keys(this.trendingData.callSubCategoriesByCategory || {}));
         // Show a message indicating no data
         this.callsDialog = {
           show: true,
           title: `No calls found for "${seriesName}"`,
           calls: [],
           category: seriesName,
           date: dateName,
           chartType: chartType
         };
       }
     },
     
     showDateDetails(date) {
       // Show all calls for a specific date
       const dateData = this.trendingData.callMainCategoriesByDate.find(item => item.date === date);
       if (dateData) {
         // Collect all calls from all categories for this date
         let allCalls = [];
         Object.keys(dateData.value || {}).forEach(category => {
           if (dateData.value[category] && dateData.value[category].calls) {
             allCalls = allCalls.concat(dateData.value[category].calls);
           }
         });
         
         this.callsDialog = {
           show: true,
           title: `All calls on ${this.formatDateForTitle(date)}`,
           calls: [...new Set(allCalls)], // Remove duplicates
           category: 'All Categories',
           date: date,
           chartType: 'dateOverview'
         };
       }
     },
     
     closeCallsDialog() {
       this.callsDialog.show = false;
     },
     
     // Export call IDs to clipboard
     async exportToClipboard() {
       try {
         // Get the call IDs from the current dialog
         const callIds = this.callsDialog.calls.filter(call => typeof call === 'string' && call.length > 0);
         
         if (callIds.length === 0) {
           this.showNotification('No call IDs to export', 'warning');
           return;
         }
         
         // Create a formatted string (comma-separated for easy pasting)
         const exportText = callIds.join(', ');
         
         // Copy to clipboard
         await navigator.clipboard.writeText(exportText);
         
         this.showNotification(`Exported ${callIds.length} call IDs to clipboard`, 'success');
         
         console.log('Exported call IDs:', callIds);
         
       } catch (error) {
         console.error('Failed to copy to clipboard:', error);
         this.showNotification('Failed to copy to clipboard', 'error');
       }
     },

     // Export call IDs to Google Sheets
     async exportToGoogleSheets() {
       try {
         this.exportingToSheets = true;
         
         // Get the call IDs from the current dialog
         const callIds = this.callsDialog.calls.filter(call => typeof call === 'string' && call.length > 0);
         
         if (callIds.length === 0) {
           this.showNotification('No call IDs to export', 'warning');
           return;
         }

         // Prepare the data for the Google Sheets export
         const exportData = {
           title: `Call Analysis Export - ${this.callsDialog.title}`,
           subtitle: `${this.callsDialog.category} - ${this.callsDialog.date}`,
           callIds: callIds,
           metadata: {
             category: this.callsDialog.category,
             date: this.callsDialog.date,
             chartType: this.callsDialog.chartType,
             exportDate: new Date().toISOString(),
             totalCalls: callIds.length
           }
         };

         // Add folder ID if specified
         if (this.exportFolderId) {
           exportData.folderId = this.exportFolderId;
         }

         // Add fields to export if specified
         if (this.exportFields && Array.isArray(this.exportFields) && this.exportFields.length > 0) {
           exportData.fields = this.exportFields;
         }

         // Call the backend function to create the Google Sheet
         const response = await axios.post('https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/export_callanalysis_to_sheets', exportData);
         
         if (response.data && response.data.sheetUrl) {
           const folderInfo = response.data.folderMoved ? ' (saved to specified folder)' : '';
           const recordCount = response.data.recordsExported || callIds.length;
           const fieldCount = response.data.fieldsExported ? response.data.fieldsExported.length : 'multiple';
           this.showNotification(`Successfully exported ${recordCount} call records with ${fieldCount} fields to Google Sheets${folderInfo}!`, 'success');
           
           // Open the created sheet in a new tab
           window.open(response.data.sheetUrl, '_blank');
           
           console.log('Exported to Google Sheets:', response.data);
         } else {
           throw new Error('Invalid response from export function');
         }
         
       } catch (error) {
         console.error('Failed to export to Google Sheets:', error);
         this.showNotification('Failed to export to Google Sheets. Please try again.', 'error');
       } finally {
         this.exportingToSheets = false;
       }
     },
     
     // Export to interactive tool
     async exportToInteractiveTool() {
       console.log('exportToInteractiveTool called, current loading state:', this.exportingToInteractive);
       try {
         // Set loading state
         console.log('Setting loading state to true');
         this.exportingToInteractive = true;
         console.log('Loading state after setting:', this.exportingToInteractive);
         
         // Force Vue to update the DOM
         await this.$nextTick();
         
         // Add a small delay to ensure the loading spinner is visible
         await new Promise(resolve => setTimeout(resolve, 100));
         
         // Get the call IDs from the current dialog
         const callIds = this.callsDialog.calls.filter(call => typeof call === 'string' && call.length > 0);
         
         if (callIds.length === 0) {
           this.showNotification('No call IDs to export', 'warning');
           console.log('No call IDs found, clearing loading state');
           this.exportingToInteractive = false;
           return;
         }
         
         // Load the actual call data from Firestore
         const db = getFirestore();
         const callsCollection = collection(db, 'cc_callanalysis_calls');
         const selectedCalls = [];
         
         // Load calls in batches (Firestore limit is 10 for 'in' queries)
         const batchSize = 10;
         for (let i = 0; i < callIds.length; i += batchSize) {
           const batch = callIds.slice(i, i + batchSize);
           const batchQuery = firestoreQuery(
             callsCollection,
             where('__name__', 'in', batch)
           );
           
           const batchSnapshot = await getDocs(batchQuery);
           batchSnapshot.forEach(doc => {
             selectedCalls.push({
               id: doc.id,
               ...doc.data()
             });
           });
         }
         
         // Create shared data object
         const sharedData = {
           selectedCalls: selectedCalls,
           selectedCallsMetadata: {
             source: 'CallAnalysisTrends',
             category: this.callsDialog.category,
             date: this.callsDialog.date,
             chartType: this.callsDialog.chartType,
             title: this.callsDialog.title,
             totalCalls: selectedCalls.length,
             exportedAt: new Date().toISOString(),
             dateRange: {
               start: this.dateRange.start,
               end: this.dateRange.end
             }
           },
           dateRange: {
             start: this.dateRange.start,
             end: this.dateRange.end
           }
         };
         
         // Show notification and close dialog
         this.showNotification(`🚀 Opening Interactive Analysis with ${selectedCalls.length} specific calls from ${this.callsDialog.title}...`, 'info');
         this.closeCallsDialog();
         
         console.log('CallAnalysisTrends: Export data prepared:', sharedData);
         
         // Emit event to parent to switch to Interactive tab with data
         console.log('CallAnalysisTrends: Emitting switch-to-interactive event');
         this.$emit('switch-to-interactive', sharedData);
         console.log('CallAnalysisTrends: switch-to-interactive event emitted');
         
       } catch (error) {
         console.error('Failed to export to interactive tool:', error);
         this.showNotification('Failed to prepare export', 'error');
       } finally {
         // Clear loading state
         console.log('Clearing loading state');
         this.exportingToInteractive = false;
       }
     },
     
     // Show notification helper
     showNotification(message, color = 'success') {
       this.snackbar = {
         show: true,
         message: message,
         color: color
       };
     },
     
     drillDownToSubCategories(mainCategory) {
       console.log('Drilling down to sub-categories for:', mainCategory);
       
       // Check if this main category exists in our mapping
       if (!this.categoryMapping[mainCategory]) {
         console.warn('No sub-categories found for main category:', mainCategory);
         return;
       }
       
       // Set the selected main category to filter sub-categories
       this.selectedMainCategory = mainCategory;
       
       // Regenerate the sub-categories charts with filtered data
       this.regenerateSubCategoriesCharts();
       
       // Scroll to sub-categories section for better UX
       this.$nextTick(() => {
         // Find the sub-categories heading and scroll to it
         const headings = document.querySelectorAll('h1');
         const subCategoriesHeading = Array.from(headings).find(h => h.textContent.includes('Call Sub Categories'));
         if (subCategoriesHeading) {
           subCategoriesHeading.scrollIntoView({ behavior: 'smooth', block: 'start' });
         }
       });
     },
     
     clearCategoryFilter() {
       console.log('Clearing category filter');
       this.selectedMainCategory = null;
       this.regenerateSubCategoriesCharts();
     },
     
     regenerateSubCategoriesCharts() {
       // Regenerate sub-categories charts with current filter
       this.$set(this.trendingCharts, 'callSubCategoriesByDate', this.generateSubCategoriesByDateChart());
       this.$set(this.trendingCharts, 'callSubCategoriesByCategory', this.generateSubCategoriesByCategoryChart());
       
       // Re-setup click detection for the updated charts
       this.$nextTick(() => {
         this.setupChartGridClickDetection('subCategoriesChart', 'subCategories', 'vertical');
         this.setupChartGridClickDetection('subCategoriesCategoryChart', 'subCategoriesCategory', 'horizontal');
       });
     },
     
     async findCallsByPhoneNumber(phoneNumber) {
       console.log('Searching for calls with phone number:', phoneNumber);
       
       // Query the cc_callanalysis_calls collection for actual call records
       return await this.searchRawDataForPhoneNumber(phoneNumber);
     },
     
     async searchRawDataForPhoneNumber(targetPhoneNumber) {
       console.log('Searching raw data for phone number:', targetPhoneNumber);
       
       try {
         const db = getFirestore();
         const callsCollection = collection(db, 'cc_callanalysis_calls');
         
         // Create date range for the query
         const startDate = this.dateRange.start;
         const endDate = this.dateRange.end;
         
         console.log(`Querying cc_callanalysis_calls for phone number ${targetPhoneNumber} between ${startDate} and ${endDate}`);
         
         // Query calls collection for this phone number within the date range
         // Note: You may need to adjust the field names based on your actual schema
         const callsQuery = firestoreQuery(
           callsCollection,
           where('phone_number', '==', targetPhoneNumber),
           where('date', '>=', startDate),
           where('date', '<=', endDate),
           limit(100) // Limit to prevent overwhelming results
         );
         
         const callsSnapshot = await getDocs(callsQuery);
         const foundCalls = [];
         
         callsSnapshot.forEach(doc => {
           foundCalls.push(doc.id); // Use document ID as call ID
         });
         
                   console.log(`Found ${foundCalls.length} calls for phone number ${targetPhoneNumber}`);
          
          // Debug: Check if there's a discrepancy with the chart data
          const callerData = this.trendingData.repeatCallersProcessed.find(([phone, count]) => 
            phone === targetPhoneNumber
          );
          if (callerData) {
            const chartCount = callerData[1];
            if (foundCalls.length !== chartCount) {
              console.warn(`DATA DISCREPANCY: Chart shows ${chartCount} calls but database query found ${foundCalls.length} calls for ${targetPhoneNumber}`);
              console.warn('This suggests an issue with the daily aggregation process or phone number formatting');
            }
          }
         
         if (foundCalls.length > 0) {
           return foundCalls;
         } else {
           // No calls found - return informational message
           return [
             `📞 ${targetPhoneNumber}`,
             '⚠️ No calls found in selected date range',
             `Searched ${startDate} to ${endDate} but found no matching calls.`,
             'This could mean the calls are outside the date range or the phone number format doesn\'t match.'
           ];
         }
         
       } catch (error) {
         console.error('Error querying calls collection:', error);
         
         // Return error message
         return [
           `📞 ${targetPhoneNumber}`,
           '⚠️ Error searching for calls',
           `Database query failed: ${error.message}`,
           'Please try again or contact support if the issue persists.'
         ];
       }
     },
     
     openCall(callId) {
       this.callId = callId;
       this.callRecordingDetailsDialog = true;
       this.closeCallsDialog();
     },
    
    // -- Function to fetch callers data from new collection ---
    async getCallersData(startDate, endDate) {
      try {
        console.log(`Fetching callers data for date range: ${startDate} to ${endDate}`);
        
        const url = `https://northamerica-northeast1-hydro-ottawa-ai.cloudfunctions.net/get_callanalysis_callers?startDate=${startDate}&endDate=${endDate}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          console.error('Failed to fetch callers data:', response.status, response.statusText);
          return [];
        }
        
        const result = await response.json();
        
        if (result.success) {
          console.log(`Successfully fetched callers data: ${result.count} days`);
          return result.data;
        } else {
          console.error('API returned error:', result.error);
          return [];
        }
        
      } catch (error) {
        console.error('Error fetching callers data:', error);
        return [];
      }
    },
     
     // -- Function to initialize the dashboard data ---
    async getTrendsData() {
      this.loading = true;
      this.errorMessage = '';
      this.dataStatus = null;
      
      const db = getFirestore();
      const trendsCollection = collection(db, 'cc_callanalysis_daily');
      
      // Only set default date range if not already set
      if (!this.dateRange.start || !this.dateRange.end) {
        // Query for the last 30 days of data by default
        const defaultEndDate = DateTime.now().minus({ days: 2 });
        const defaultStartDate = defaultEndDate.minus({ days: 30 });
        
        // Format dates for Firestore query - using YYYY-MM-DD format (matching DB document IDs)
        const formattedStartDate = defaultStartDate.toFormat('yyyy-MM-dd');
        const formattedEndDate = defaultEndDate.toFormat('yyyy-MM-dd');
        
        this.dateRange.start = formattedStartDate;
        this.dateRange.end = formattedEndDate;
      }
      
      console.log('Fetching data for date range:', this.dateRange.start, 'to', this.dateRange.end);
      
      try {
        // Create an optimized query using Firestore's built-in range filtering
        // Since document IDs are in YYYY-MM-DD format, we can use startAt/endAt for efficient filtering
        const trendsQuery = firestoreQuery(
          trendsCollection,
          orderBy('__name__'), // Order by document ID (which is the date)
          startAt(this.dateRange.start), // Start from the start date
          endAt(this.dateRange.end + '\uf8ff'), // End at the end date (+ high unicode char for inclusive end)
          limit(200) // Reasonable limit since we're now filtering efficiently
        );
        
        console.log(`Executing optimized Firestore query for date range: ${this.dateRange.start} to ${this.dateRange.end}`);
        const trendsSnapshot = await getDocs(trendsQuery);
        console.log(`Optimized query returned ${trendsSnapshot.size} documents (filtered at database level)`);
        
        if (!trendsSnapshot.empty) {
          // All documents are now already filtered by date range at the database level
          const filteredDocs = [];
          
          trendsSnapshot.forEach(doc => {
            const docId = doc.id;
            
            // Quick validation that the document ID is a valid date
            if (this.isValidDateString(docId)) {
              filteredDocs.push(doc);
            } else {
              console.warn(`Document ${docId} is not a valid date string, skipping`);
            }
          });
          
          console.log(`Using ${filteredDocs.length} valid date documents from optimized query`);
          
          if (filteredDocs.length > 0) {
            // Combine data from all documents
            const combinedTrendingData = this.emptyTrendingData();
            const combinedTrendingCharts = this.emptyTrendingCharts();

            combinedTrendingData.callMainCategoriesByCategory = {}
            combinedTrendingData.callMainCategoriesByCategory['Account Management'] = { count: 0, calls: [] }
            combinedTrendingData.callMainCategoriesByCategory['Billing and Payments'] = { count: 0, calls: [] };
            combinedTrendingData.callMainCategoriesByCategory['Service and Connections'] = { count: 0, calls: [] };
            combinedTrendingData.callMainCategoriesByCategory['Rates and Plans'] = { count: 0, calls: [] };
            combinedTrendingData.callMainCategoriesByCategory['Outages and Emergencies'] = { count: 0, calls: [] };
            combinedTrendingData.callMainCategoriesByCategory['Program and Service Support'] = { count: 0, calls: [] };
            combinedTrendingData.callMainCategoriesByCategory['Other Inquiries'] = { count: 0, calls: [] };
            combinedTrendingData.callMainCategoriesByCategory['Non-categorized inquiries'] = { count: 0, calls: [] };
            combinedTrendingData.callMainCategoriesByDate = []

            // Initialize sub categories
            combinedTrendingData.callSubCategoriesByCategory = {};
            combinedTrendingData.callSubCategoriesByDate = [];
            
            // Sub categories initialization
            const subCategories = [
              'Account Balance & Bill Inquiries',
              'High Bill / Usage Inquiries',
              'Other Billing disputes',
              'Budget billing and equal payment plans',
              'Payment methods and options',
              'Payment issues',
              'Late payments, and collections',
              'Payment arrangements and extensions',
              'Payment Confirmation',
              'Pre-authorized payments',
              'Financial Assistance & Subsidies',
              'Security deposits (questions, refunds)',
              'Rate plan options and comparisons',
              'Understanding energy Usage & Consumption',
              'Energy efficiency programs and rebates',
              'Updating account information',
              'New account setup',
              'Account closure and termination',
              'Moves - service start/stop',
              'Merging/linking multiple accounts',
              'Deceased Account Management',
              'Landlord and tenants',
              'MyAccount Technical Support',
              'Service Requests',
              'Vegetation Management',
              'Power outages',
              'Power Quality & Reliability',
              'Safety concerns during outages',
              'Generator inquiries and safety',
              'Battery Loan',
              'Outage communicatons',
              'Distributed Energy Resources (DER)',
              'Privacy policy and data protection',
              'Feedback and suggestions',
              'Scam / Fraud',
              'Other'
            ];
            
            subCategories.forEach(subCategory => {
              combinedTrendingData.callSubCategoriesByCategory[subCategory] = { count: 0, calls: [] };
            });

            // Initialize customer satisfaction data
            combinedTrendingData.customerSatisfactionByDate = [];
            combinedTrendingData.customerSatisfactionByCategory = {
              'Agent Apologies': { count: 0, calls: [] },
              'Long Call': { count: 0, calls: [] },
              'Long Hold or Silence': { count: 0, calls: [] },
              'Negative Sentiment': { count: 0, calls: [] },
              'Repeated Explanations': { count: 0, calls: [] },
              'Technical Issue (Agent)': { count: 0, calls: [] },
              'Technical Issue (Customer)': { count: 0, calls: [] },
              'Transfers or Escalations': { count: 0, calls: [] }
            };

            // Initialize sentiment data
            combinedTrendingData.sentimentByDate = [];
            combinedTrendingData.sentimentByCategory = {
              'Very Negative': { count: 0, calls: [] },
              'Negative': { count: 0, calls: [] },
              'Neutral': { count: 0, calls: [] },
              'Positive': { count: 0, calls: [] },
              'Very Positive': { count: 0, calls: [] }
            };

            // Initialize sentiment trajectories data (dynamic categories)
            combinedTrendingData.sentimentTrajectoriesByDate = [];
            combinedTrendingData.sentimentTrajectoriesByCategory = {};

            // Initialize call duration data
            combinedTrendingData.callDurationByDate = [];
            combinedTrendingData.callDurationByCategory = {
              '0-2': { count: 0, calls: [] },
              '2-4': { count: 0, calls: [] },
              '4-6': { count: 0, calls: [] },
              '6-8': { count: 0, calls: [] },
              '8-10': { count: 0, calls: [] },
              '10-12': { count: 0, calls: [] },
              '12-14': { count: 0, calls: [] },
              '14-16': { count: 0, calls: [] },
              '16-18': { count: 0, calls: [] },
              '18-20': { count: 0, calls: [] },
              '20+': { count: 0, calls: [] }
            };

            filteredDocs.forEach(doc => {
              const data = doc.data();
              
              // ------------ Call Volume ------------
              // Groupbed by date
              combinedTrendingData.callVolume.push({
                date: doc.id,
                value: data.total_calls
              });

              // ------------ Call Main Categories ------------
              // Grouped by date and stacked by category
              combinedTrendingData.callMainCategoriesByDate.push({
                date: doc.id,
                value: data.main_categories
              });

              // ------------ Call Main Categories ------------
              // Grouped by category
              if (data.main_categories && data.main_categories['Account Management']) {
                combinedTrendingData.callMainCategoriesByCategory['Account Management'].count += data.main_categories['Account Management'].total || 0;
                if (data.main_categories['Account Management'].calls) {
                  combinedTrendingData.callMainCategoriesByCategory['Account Management'].calls.push(...data.main_categories['Account Management'].calls);
                }
              }

              if (data.main_categories && data.main_categories['Billing and Payments']) {
                combinedTrendingData.callMainCategoriesByCategory['Billing and Payments'].count += data.main_categories['Billing and Payments'].total || 0;
                if (data.main_categories['Billing and Payments'].calls) {
                  combinedTrendingData.callMainCategoriesByCategory['Billing and Payments'].calls.push(...data.main_categories['Billing and Payments'].calls);
                }
              }

              if (data.main_categories && data.main_categories['Service and Connections']) {
                combinedTrendingData.callMainCategoriesByCategory['Service and Connections'].count += data.main_categories['Service and Connections'].total || 0;
                if (data.main_categories['Service and Connections'].calls) {
                  combinedTrendingData.callMainCategoriesByCategory['Service and Connections'].calls.push(...data.main_categories['Service and Connections'].calls);
                }
              }

              if (data.main_categories && data.main_categories['Rates and Plans']) {
                combinedTrendingData.callMainCategoriesByCategory['Rates and Plans'].count += data.main_categories['Rates and Plans'].total || 0;
                if (data.main_categories['Rates and Plans'].calls) {
                  combinedTrendingData.callMainCategoriesByCategory['Rates and Plans'].calls.push(...data.main_categories['Rates and Plans'].calls);
                }
              }

              if (data.main_categories && data.main_categories['Outages and Emergencies']) {
                combinedTrendingData.callMainCategoriesByCategory['Outages and Emergencies'].count += data.main_categories['Outages and Emergencies'].total || 0;
                if (data.main_categories['Outages and Emergencies'].calls) {
                  combinedTrendingData.callMainCategoriesByCategory['Outages and Emergencies'].calls.push(...data.main_categories['Outages and Emergencies'].calls);
                }
              }

              if (data.main_categories && data.main_categories['Program and Service Support']) {
                combinedTrendingData.callMainCategoriesByCategory['Program and Service Support'].count += data.main_categories['Program and Service Support'].total || 0;
                if (data.main_categories['Program and Service Support'].calls) {
                  combinedTrendingData.callMainCategoriesByCategory['Program and Service Support'].calls.push(...data.main_categories['Program and Service Support'].calls);
                }
              }

              if (data.main_categories && data.main_categories['Other Inquiries']) {
                combinedTrendingData.callMainCategoriesByCategory['Other Inquiries'].count += data.main_categories['Other Inquiries'].total || 0;
                if (data.main_categories['Other Inquiries'].calls) {
                  combinedTrendingData.callMainCategoriesByCategory['Other Inquiries'].calls.push(...data.main_categories['Other Inquiries'].calls);
                }
              }

              if (data.main_categories && data.main_categories['Non-categorized inquiries']) {
                combinedTrendingData.callMainCategoriesByCategory['Non-categorized inquiries'].count += data.main_categories['Non-categorized inquiries'].total || 0;
                if (data.main_categories['Non-categorized inquiries'].calls) {
                  combinedTrendingData.callMainCategoriesByCategory['Non-categorized inquiries'].calls.push(...data.main_categories['Non-categorized inquiries'].calls);
                }
              }

              // ------------ Call Sub Categories ------------
              // Grouped by date and stacked by category
              if (data.sub_categories) {
                combinedTrendingData.callSubCategoriesByDate.push({
                  date: doc.id,
                  value: data.sub_categories
                });
              }
              
              // Aggregate sub categories across all dates
              if (data.sub_categories) {
                Object.keys(data.sub_categories).forEach(subCategory => {
                  if (combinedTrendingData.callSubCategoriesByCategory[subCategory]) {
                    combinedTrendingData.callSubCategoriesByCategory[subCategory].count += data.sub_categories[subCategory].total || 0;
                    if (data.sub_categories[subCategory].calls) {
                      combinedTrendingData.callSubCategoriesByCategory[subCategory].calls.push(...data.sub_categories[subCategory].calls);
                    }
                  }
                });
              }

              // ------------ Customer Satisfaction ------------
              // Aggregate satisfaction data by date
              if (data.satisfaction) {
                combinedTrendingData.customerSatisfactionByDate.push({
                  date: doc.id,
                  value: data.satisfaction.markers
                });
              }
              
              // Aggregate satisfaction factors across all dates
              if (data.satisfaction) {
                const factors = [
                  'Agent Apologies',
                  'Long Call',
                  'Long Hold or Silence',
                  'Negative Sentiment',
                  'Repeated Explanations',
                  'Technical Issue (Agent)',
                  'Technical Issue (Customer)',
                  'Transfers or Escalations'
                ];
                
                factors.forEach(factor => {
                  if (data.satisfaction.markers[factor] && data.satisfaction.markers[factor].total !== undefined) {
                    combinedTrendingData.customerSatisfactionByCategory[factor].count += data.satisfaction.markers[factor].total || 0;
                    if (data.satisfaction.markers[factor].calls) {
                      combinedTrendingData.customerSatisfactionByCategory[factor].calls.push(...data.satisfaction.markers[factor].calls);
                    }
                  }
                });
              }

              // ------------ Sentiment Buckets ------------
              // Aggregate sentiment buckets by date
              if (data.sentiment && data.sentiment.buckets) {
                combinedTrendingData.sentimentByDate.push({
                  date: doc.id,
                  value: data.sentiment.buckets
                });
              }
              
              // Aggregate sentiment buckets across all dates
              if (data.sentiment && data.sentiment.buckets) {
                const sentimentBuckets = [
                  'Very Negative',
                  'Negative', 
                  'Neutral',
                  'Positive',
                  'Very Positive'
                ];
                
                sentimentBuckets.forEach(bucket => {
                  if (data.sentiment.buckets[bucket] && data.sentiment.buckets[bucket].calls) {
                    combinedTrendingData.sentimentByCategory[bucket].count += data.sentiment.buckets[bucket].calls.length || 0;
                    combinedTrendingData.sentimentByCategory[bucket].calls.push(...data.sentiment.buckets[bucket].calls);
                  }
                });
              }

              // ------------ Sentiment Trajectories ------------
              // Aggregate sentiment trajectories by date
              if (data.sentiment && data.sentiment.trajectories) {
                combinedTrendingData.sentimentTrajectoriesByDate.push({
                  date: doc.id,
                  value: data.sentiment.trajectories
                });
              }
              
              // Aggregate sentiment trajectories across all dates (dynamic categories)
              if (data.sentiment && data.sentiment.trajectories) {
                Object.keys(data.sentiment.trajectories).forEach(trajectory => {
                  // Initialize trajectory category if it doesn't exist
                  if (!combinedTrendingData.sentimentTrajectoriesByCategory[trajectory]) {
                    combinedTrendingData.sentimentTrajectoriesByCategory[trajectory] = { count: 0, calls: [] };
                  }
                  
                  const trajectoryData = data.sentiment.trajectories[trajectory];
                  if (trajectoryData && trajectoryData.total !== undefined) {
                    combinedTrendingData.sentimentTrajectoriesByCategory[trajectory].count += trajectoryData.total || 0;
                    if (trajectoryData.calls) {
                      combinedTrendingData.sentimentTrajectoriesByCategory[trajectory].calls.push(...trajectoryData.calls);
                    }
                  }
                });
              }

              // ------------ Call Duration ------------
              // Aggregate call duration by date
              if (data.call_length_minutes) {
                combinedTrendingData.callDurationByDate.push({
                  date: doc.id,
                  value: data.call_length_minutes
                });
              }
              
              // Aggregate call duration across all dates
              if (data.call_length_minutes) {
                const durationBuckets = [
                  '0-2', '2-4', '4-6', '6-8', '8-10', 
                  '10-12', '12-14', '14-16', '16-18', '18-20', '20+'
                ];
                
                durationBuckets.forEach(bucket => {
                  if (data.call_length_minutes[bucket] && data.call_length_minutes[bucket].calls) {
                    combinedTrendingData.callDurationByCategory[bucket].count += data.call_length_minutes[bucket].calls.length || 0;
                    combinedTrendingData.callDurationByCategory[bucket].calls.push(...data.call_length_minutes[bucket].calls);
                  }
                });
              }

              // ------------ Repeat Callers ------------
              // Note: Callers data is now loaded separately from cc_callanalysis_callers collection

              // console.log('repeat_callers', data.repeat_callers);
              // combinedTrendingData.repeatCallers.push(data.repeat_callers);


              // combinedTrendingData.customerEffort.push(data.customer_effort_aggregation);
              // combinedTrendingData.escalations.push(data.escalations);
              // combinedTrendingData.longCalls.push(data.long_calls);

              // this.mergeDataInto(combinedData, data);
              
              // // Keep track of the actual date range from the documents
              // const docDate = doc.id; // Use document ID as the date
              // if (!actualStartDate || docDate < actualStartDate) {
              //   actualStartDate = docDate;
              // }
              // if (!actualEndDate || docDate > actualEndDate) {
              //   actualEndDate = docDate;
              // }
            });

            // ------------ Repeat Callers Processing ------------
            // Fetch callers data from separate collection
            const callersDataArray = await this.getCallersData(this.dateRange.start, this.dateRange.end);
            
            // Process callers data - aggregate phone numbers across all dates
            callersDataArray.forEach(dayCallersData => {
              if (dayCallersData.callers) {
                Object.entries(dayCallersData.callers).forEach(([phoneNumber, callCount]) => {
                  if (phoneNumber && typeof callCount === 'number') {
                    // Initialize count if this is the first time we see this number
                    if (!combinedTrendingData.repeatCallers[phoneNumber]) {
                      combinedTrendingData.repeatCallers[phoneNumber] = 0;
                    }
                    // Add the call count for this phone number from this day
                    combinedTrendingData.repeatCallers[phoneNumber] += callCount;
                  }
                });
              }
            });
            
            // Filter out callers with less than 2 calls and sort by call count
            if (combinedTrendingData.repeatCallers && Object.keys(combinedTrendingData.repeatCallers).length > 0) {
              const repeatCallersFiltered = Object.entries(combinedTrendingData.repeatCallers)
                .filter(([phoneNumber, callCount]) => callCount >= 2)
                .sort((a, b) => b[1] - a[1]); // Sort by call count descending
              
              combinedTrendingData.repeatCallersProcessed = repeatCallersFiltered;
            } else {
              combinedTrendingData.repeatCallersProcessed = [];
            }

            console.log('repeatCallers from new collection:', combinedTrendingData.repeatCallers, combinedTrendingData.repeatCallersProcessed);
            
            // Debug: Let's see what the raw callers data looks like from the new collection
            console.log('DEBUG: Raw callers data from new collection:');
            callersDataArray.forEach(dayData => {
              if (dayData.callers) {
                const totalCalls = Object.values(dayData.callers).reduce((sum, count) => sum + count, 0);
                console.log(`Date ${dayData.date}: ${Object.keys(dayData.callers).length} unique callers, ${totalCalls} total calls`, dayData.callers);
              }
            });
            



            // Debug logging to see what data we have
            console.log('Combined trending data:', combinedTrendingData);
            console.log('Call main categories by category:', combinedTrendingData.callMainCategoriesByCategory);
            console.log('Call main categories by date:', combinedTrendingData.callMainCategoriesByDate);
            
            this.trendingData = combinedTrendingData;
            this.trendingCharts = combinedTrendingCharts;
            
            // this.trendingData = combinedData;
            this.loading = false;
            
            // --- Initialize charts ---
            this.trendingCharts.callVolume = this.generateCallVolumeChart();
            
            this.trendingCharts.callMainCategoriesByDate = this.generateMainCategoriesByDateChart();
            this.trendingCharts.callMainCategoriesByCategory = this.generateMainCategoriesByCategoryChart();

            this.trendingCharts.callSubCategoriesByDate = this.generateSubCategoriesByDateChart();
            this.trendingCharts.callSubCategoriesByCategory = this.generateSubCategoriesByCategoryChart();

            this.trendingCharts.customerSatisfactionByDate = this.generateCustomerSatisfactionByDateChart();
            this.trendingCharts.customerSatisfactionByCategory = this.generateCustomerSatisfactionByCategoryChart();

            this.trendingCharts.sentimentByDate = this.generateSentimentByDateChart();
            this.trendingCharts.sentimentByCategory = this.generateSentimentByCategoryChart();
            this.trendingCharts.sentimentTrajectoriesByDate = this.generateSentimentTrajectoriesByDateChart();
            this.trendingCharts.sentimentTrajectoriesByCategory = this.generateSentimentTrajectoriesByCategoryChart();

            this.trendingCharts.callDurationByDate = this.generateCallDurationByDateChart();
            this.trendingCharts.callDurationByCategory = this.generateCallDurationByCategoryChart();

            this.trendingCharts.repeatCallers = this.generateRepeatCallersChart();
            
            // this.repeatCallersChart = this.generateRepeatCallersChart();
            // this.customerEffortAggregationChart = this.generateCustomerEffortAggregationChart();
            
            // Set up advanced click detection after charts are rendered
            this.$nextTick(() => {
              this.setupAdvancedClickDetection();
            });
            
            // Update picker dates to match the current range
            // this.updatePickerDates();
            
            // Show data status
            // this.dataStatus = {
            //   type: 'success',
            //   message: `Successfully loaded data from ${filteredDocs.length} days within the selected date range.`
            // };
          } else {
            // Initialize empty data structure so UI doesn't break
            this.trendingData = this.emptyTrendingData();
            this.trendingCharts = this.emptyTrendingCharts();
            this.loading = false;
            this.errorMessage = 'No data available for the selected date range.';
          }
        } else {
          console.log('No documents found in collection!');
          // Initialize empty data structure so UI doesn't break
          this.trendingData = this.emptyTrendingData();
          this.trendingCharts = this.emptyTrendingCharts();
          this.loading = false;
          this.errorMessage = 'No data available.';
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.loading = false;
        this.trendingData = this.emptyTrendingData();
        this.trendingCharts = this.emptyTrendingCharts();
        this.errorMessage = `Error fetching data: ${error.message}`;
      }
    },

    // Add method to get available dates from the database
    async loadAvailableDates() {
      try {
        const db = getFirestore();
        const trendsCollection = collection(db, 'cc_callanalysis_daily');
        
        // Get all documents to determine available date range
        const trendsQuery = firestoreQuery(trendsCollection, limit(200));
        const trendsSnapshot = await getDocs(trendsQuery);
        
        const availableDates = [];
        
        trendsSnapshot.forEach(doc => {
          const docId = doc.id;
          if (this.isValidDateString(docId)) {
            availableDates.push(docId);
          }
        });
        
        if (availableDates.length > 0) {
          // Sort dates to find min and max
          availableDates.sort();
          this.availableDateRange.start = availableDates[0];
          this.availableDateRange.end = availableDates[availableDates.length - 1];
          
          // Set min/max selectable dates for the date picker
          this.minSelectableDate = availableDates[0];
          this.maxSelectableDate = availableDates[availableDates.length - 1];
        }
      } catch (error) {
        console.error('Error loading available dates:', error);
      }
    },

    // Method to check if a date is allowed (exists in database)
    async isDateAllowed(date) {
      if (!date) return false;
      
      try {
        const db = getFirestore();
        const docRef = doc(db, 'cc_callanalysis_daily', date);
        const docSnap = await getDoc(docRef);
        return docSnap.exists();
      } catch (error) {
        console.error('Error checking date availability:', error);
        return false;
      }
    },


    
    resetDateRange() {
      // Reset to the last 30 days
      const now = DateTime.now().minus({ days: 2 });
      const defaultStart = now.minus({ days: 30 });
      
      // Format for display and store for query (using YYYY-MM-DD to match DB)
      this.dateRange.start = defaultStart.toFormat('yyyy-MM-dd');
      this.dateRange.end = now.toFormat('yyyy-MM-dd');
      this.startDate = this.dateRange.start;
      this.endDate = this.dateRange.end;
      
      // Reset the selected date range for the picker
      this.selectedDateRange = [this.dateRange.start, this.dateRange.end];
      
      console.log('Date range reset:', this.dateRange.start, 'to', this.dateRange.end);
      
      this.datePickerMenu = false; // Close the menu if open
      this.dateValidationMessage = '';
      this.getTrendsData();
    },
    
    // Helper method to initialize an empty data structure
    emptyTrendingData() {
      return {
        callVolume: [],
        callMainCategoriesByDate: [],
        callMainCategoriesByCategory: {},
        callSubCategoriesByDate: [],
        callSubCategoriesByCategory: {},
        customerSatisfactionByDate: [],
        customerSatisfactionByCategory: {},
        sentimentByDate: [],
        sentimentByCategory: {},
        sentimentTrajectoriesByDate: [],
        sentimentTrajectoriesByCategory: {},
        callDurationByDate: [],
        callDurationByCategory: {},
        repeatCallers: {},
        repeatCallersProcessed: [],
        customerEffort: [],
        escalations: [],
        longCalls: [],
        sentimentScores: []
      }
    },
    emptyTrendingCharts() {
      return {
        callVolume: null,
        callMainCategoriesByDate: null,
        callMainCategoriesByCategory: null,
        callSubCategoriesByDate: null,
        callSubCategoriesByCategory: null,
        customerSatisfactionByDate: null,
        customerSatisfactionByCategory: null,
        sentimentByDate: null,
        sentimentByCategory: null,
        sentimentTrajectoriesByDate: null,
        sentimentTrajectoriesByCategory: null,
        callDurationByDate: null,
        callDurationByCategory: null,
        repeatCallers: null,
        customerEffort: null,
        escalations: null,
        longCalls: null,
        sentimentScores: null,
      }
    },
    
    
    previousInsight() {
      this.insightIndex = this.insightIndex - 1;
      if (this.insightIndex < 0) {
        this.insightIndex = this.trendingData?.monthly_top_insights?.length - 1;
      }
    },
    nextInsight() {
      this.insightIndex = this.insightIndex + 1;
      if (this.insightIndex >= this.trendingData?.monthly_top_insights?.length) {
        this.insightIndex = 0;
      }
    },
    openCall(id) {
      this.callId = id;
      this.callRecordingDetailsDialog = true;
    },

    // --- Open Interactive Analysis with current date range ---
    openInteractiveAnalysis() {
      console.log('Opening Interactive Analysis with date range:', this.dateRange.start, 'to', this.dateRange.end);
      
      this.$emit('switch-to-interactive', {
        dateRange: {
          start: this.dateRange.start,
          end: this.dateRange.end
        },
        source: 'trends',
        metadata: {
          totalCalls: this.totalCallsInRange,
          dateRange: this.dateRange
        }
      });
    },

    // ================================ Charts ================================
    generateCallVolumeChart() {
      const displayData = this.getDisplayData(this.trendingData.callVolume);
      
      return {
        grid: {
          top: 10,    // Reduce top padding
          left: 50,   // Adjust as needed
          right: 30,  // Adjust as needed  
          bottom: 60  // Space for rotated labels
        },
        tooltip: {
          trigger: 'axis',
          formatter: function (params) {
            const param = params[0];
            return `${param.name}<br/>Calls: ${param.value}`;
          }
        },
        xAxis: {
          type: 'category',
          data: displayData.map(item => this.getDisplayLabel(item.date, item.weekLabel)),
          axisLabel: {
            interval: 0,  // Force display of all labels
            rotate: 45,    // Rotate labels to avoid overlap
            show: displayData.length <= 10 ? true : false
          }
        },
        yAxis: {
          type: 'value',
        },
        series: [
          {
            data: displayData.map(item => item.value),
            type: 'line'
          }
        ]
      };
    },

    generateMainCategoriesByDateChart() {
      // This is a stacked bar chart representing the main categories within each date/week
      const categories = [
        'Account Management',
        'Billing and Payments',
        'Service and Connections',
        'Rates and Plans',
        'Outages and Emergencies',
        'Program and Service Support',
        'Other Inquiries',
        // 'Non-categorized inquiries'
      ];
      
      const displayData = this.getDisplayData(this.trendingData.callMainCategoriesByDate);
      
      // Check if we have data
      if (!displayData || displayData.length === 0) {
        return {
          title: {
            text: 'No data available',
            left: 'center'
          }
        };
      }
      
      const series = categories.map((name) => {
        return {
          name,
          type: 'bar',
          stack: 'total',
          label: {
            show: false
          },
          // Make bars more clickable
          emphasis: {
            focus: 'series'
          },
          itemStyle: {
            color: this.categoryColors[name] || '#6b7280'
          },
          data: displayData.map(item => {
            if (item.value && item.value[name] && item.value[name].total !== undefined) {
              return item.value[name].total;
            }
            return 0;
          })
        };
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: categories,
          type: 'scroll',
          orient: 'horizontal',
          left: 'center',
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        yAxis: {
          type: 'value'
        },
        xAxis: {
          type: 'category',
          data: displayData.map(item => this.getDisplayLabel(item.date, item.weekLabel)),
          axisLabel: {
            interval: 0,
            rotate: 45,
            show: displayData.length <= 10 ? true : false
          },
          // Make the entire column area clickable
          triggerEvent: true
        },
        // Enable click detection
        animation: false, // Disable animation for better click detection
        // Make cursor pointer on hover
        silent: false,
        series
      };
    },

    generateMainCategoriesByCategoryChart() {
      // This is a horizontal bar chart showing total calls by category
      const categories = [
        'Account Management',
        'Billing and Payments',
        'Service and Connections',
        'Rates and Plans',
        'Outages and Emergencies',
        'Program and Service Support',
        'Other Inquiries',
        // 'Non-categorized inquiries'
      ];
      
      // Check if we have data
      if (!this.trendingData.callMainCategoriesByCategory) {
        console.log('No callMainCategoriesByCategory data available');
        return {
          title: {
            text: 'No data available',
            left: 'center'
          }
        };
      }
      
      console.log('Generating chart with categories data:', this.trendingData.callMainCategoriesByCategory);
      
      const values = categories.map(category => {
        const categoryData = this.trendingData.callMainCategoriesByCategory[category];
        const count = categoryData ? categoryData.count : 0;
        console.log(`Category ${category}: count = ${count}`);
        return count;
      });
      
      console.log('Chart values:', values);
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            const param = params[0];
            return `${param.name}<br/>Calls: ${param.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: categories,
          axisLabel: {
            interval: 0
          }
        },
        series: [
          {
            name: 'Call Count',
            type: 'bar',
            data: values.map((value, index) => ({
              value: value,
              itemStyle: {
                color: this.categoryColors[categories[index]] || '#6b7280'
              }
            })),
            label: {
              show: true,
              position: 'right'
            }
          }
        ]
      };
    },

    generateSubCategoriesByDateChart() {
      // This is a stacked bar chart representing the sub categories within each date/week
      // Filter sub-categories based on selected main category
      let subCategories;
      
      if (this.selectedMainCategory && this.categoryMapping[this.selectedMainCategory]) {
        // Show only sub-categories for the selected main category
        subCategories = this.categoryMapping[this.selectedMainCategory];
      } else {
        // Show all sub-categories
        subCategories = [
          'Account Balance & Bill Inquiries',
          'High Bill / Usage Inquiries',
          'Other Billing disputes',
          'Budget billing and equal payment plans',
          'Payment methods and options',
          'Payment issues',
          'Late payments, and collections',
          'Payment arrangements and extensions',
          'Payment Confirmation',
          'Pre-authorized payments',
          'Financial Assistance & Subsidies',
          'Security deposits (questions, refunds)',
          'Rate plan options and comparisons',
          'Understanding energy Usage & Consumption',
          'Energy efficiency programs and rebates',
          'Updating account information',
          'New account setup',
          'Account closure and termination',
          'Moves - service start/stop',
          'Merging/linking multiple accounts',
          'Deceased Account Management',
          'Landlord and tenants',
          'MyAccount Technical Support',
          'Service Requests',
          'Vegetation Management',
          'Power outages',
          'Power Quality & Reliability',
          'Safety concerns during outages',
          'Generator inquiries and safety',
          'Battery Loan',
          'Outage communicatons',
          'Distributed Energy Resources (DER)',
          'Privacy policy and data protection',
          'Feedback and suggestions',
          'Scam / Fraud',
          'Other'
        ];
      }
      
      const displayData = this.getDisplayData(this.trendingData.callSubCategoriesByDate);
      
      // Check if we have data
      if (!displayData || displayData.length === 0) {
        return {
          title: {
            text: 'No sub categories data available',
            left: 'center'
          }
        };
      }
      
      const series = subCategories.map((name) => {
        return {
          name,
          type: 'bar',
          stack: 'total',
          label: {
            show: false
          },
          itemStyle: {
            color: this.getSubCategoryColor(name, subCategories)
          },
          data: displayData.map(item => {
            if (item.value && item.value[name] && item.value[name].total !== undefined) {
              return item.value[name].total;
            }
            return 0;
          })
        };
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: subCategories,
          type: 'scroll',
          orient: 'horizontal',
          left: 'center',
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        yAxis: {
          type: 'value'
        },
        xAxis: {
          type: 'category',
          data: displayData.map(item => this.getDisplayLabel(item.date, item.weekLabel)),
          axisLabel: {
            interval: 0,
            rotate: 45,
            show: displayData.length <= 10 ? true : false
          }
        },
        series
      };
    },

    generateSubCategoriesByCategoryChart() {
      // This is a horizontal bar chart showing total calls by sub category
      // Filter sub-categories based on selected main category
      let subCategories;
      
      if (this.selectedMainCategory && this.categoryMapping[this.selectedMainCategory]) {
        // Show only sub-categories for the selected main category
        subCategories = this.categoryMapping[this.selectedMainCategory];
      } else {
        // Show all sub-categories
        subCategories = [
          'Account Balance & Bill Inquiries',
          'High Bill / Usage Inquiries',
          'Other Billing disputes',
          'Budget billing and equal payment plans',
          'Payment methods and options',
          'Payment issues',
          'Late payments, and collections',
          'Payment arrangements and extensions',
          'Payment Confirmation',
          'Pre-authorized payments',
          'Financial Assistance & Subsidies',
          'Security deposits (questions, refunds)',
          'Rate plan options and comparisons',
          'Understanding energy Usage & Consumption',
          'Energy efficiency programs and rebates',
          'Updating account information',
          'New account setup',
          'Account closure and termination',
          'Moves - service start/stop',
          'Merging/linking multiple accounts',
          'Deceased Account Management',
          'Landlord and tenants',
          'MyAccount Technical Support',
          'Service Requests',
          'Vegetation Management',
          'Power outages',
          'Power Quality & Reliability',
          'Safety concerns during outages',
          'Generator inquiries and safety',
          'Battery Loan',
          'Outage communicatons',
          'Distributed Energy Resources (DER)',
          'Privacy policy and data protection',
          'Feedback and suggestions',
          'Scam / Fraud',
          'Other'
        ];
      }
      
      // Check if we have data
      if (!this.trendingData.callSubCategoriesByCategory) {
        console.log('No callSubCategoriesByCategory data available');
        return {
          title: {
            text: 'No sub categories data available',
            left: 'center'
          }
        };
      }
      
      console.log('Generating sub categories chart with data:', this.trendingData.callSubCategoriesByCategory);
      
      const values = subCategories.map(category => {
        const categoryData = this.trendingData.callSubCategoriesByCategory[category];
        const count = categoryData ? categoryData.count : 0;
        return count;
      });
      
      // Filter out categories with zero values for a cleaner chart
      const filteredData = subCategories
        .map((category, index) => ({ category, value: values[index] }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value); // Sort by value descending
      
      const filteredCategories = filteredData.map(item => item.category);
      const filteredValues = filteredData.map(item => item.value);
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            const param = params[0];
            return `${param.name}<br/>Calls: ${param.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: filteredCategories,
          axisLabel: {
            interval: 0
          }
        },
        series: [
          {
            name: 'Call Count',
            type: 'bar',
            data: filteredValues.map((value, index) => ({
              value: value,
              itemStyle: {
                color: this.getSubCategoryColor(filteredCategories[index], filteredCategories)
              }
            })),
            label: {
              show: true,
              position: 'right'
            }
          }
        ]
      };
    },

    generateCustomerSatisfactionByDateChart() {
      // This shows satisfaction factors count over time
      
      const displayData = this.getDisplayData(this.trendingData.customerSatisfactionByDate);
      
      // Check if we have data
      if (!displayData || displayData.length === 0) {
        return {
          title: {
            text: 'No customer satisfaction data available',
            left: 'center'
          }
        };
      }
      
      const satisfactionFactors = [
        'Agent Apologies',
        'Long Call',
        'Long Hold or Silence',
        'Negative Sentiment',
        'Repeated Explanations',
        'Technical Issue (Agent)',
        'Technical Issue (Customer)',
        'Transfers or Escalations'
      ];
      
      const colors = [
        '#ee6767', '#f87171', '#fac858', '#fbbf24', 
        '#92cc76', '#60a5fa', '#a78bfa', '#f59e0b'
      ];
      
      // Create series for each satisfaction factor
      const series = satisfactionFactors.map((factor, index) => {
        return {
          name: factor,
          type: 'bar',
          stack: 'total',
          data: displayData.map(item => {
            if (item.value && item.value[factor] && item.value[factor].total !== undefined) {
              return item.value[factor].total;
            }
            return 0;
          }),
          itemStyle: {
            color: colors[index % colors.length]
          }
        };
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross'
          }
        },
        legend: {
          data: satisfactionFactors,
          type: 'scroll',
          orient: 'horizontal',
          left: 'center',
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: displayData.map(item => this.getDisplayLabel(item.date, item.weekLabel)),
          axisLabel: {
            interval: 0,
            rotate: 45,
            show: displayData.length <= 10 ? true : false
          }
        },
        yAxis: {
          type: 'value',
          name: 'Count',
          min: 0
        },
        series
      };
    },

    generateCustomerSatisfactionByCategoryChart() {
      // This is a horizontal bar chart showing satisfaction factors
      const satisfactionFactors = [
        'Agent Apologies',
        'Long Call',
        'Long Hold or Silence',
        'Negative Sentiment',
        'Repeated Explanations',
        'Technical Issue (Agent)',
        'Technical Issue (Customer)',
        'Transfers or Escalations'
      ];
      
      // Check if we have data
      if (!this.trendingData.customerSatisfactionByCategory) {
        console.log('No customerSatisfactionByCategory data available');
        return {
          title: {
            text: 'No customer satisfaction data available',
            left: 'center'
          }
        };
      }
      
      console.log('Generating satisfaction chart with data:', this.trendingData.customerSatisfactionByCategory);
      
      const values = satisfactionFactors.map(factor => {
        const factorData = this.trendingData.customerSatisfactionByCategory[factor];
        return factorData ? factorData.count : 0;
      });
      
      // Filter out factors with zero values for a cleaner chart
      const filteredData = satisfactionFactors
        .map((factor, index) => ({ 
          factor: factor, 
          value: values[index] 
        }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value); // Sort by value descending
      
      const filteredFactors = filteredData.map(item => item.factor);
      const filteredValues = filteredData.map(item => item.value);
      
      // Color scheme for satisfaction factors (using warmer colors for negative factors)
      const colors = [
        '#ee6767', '#f87171', '#fac858', '#fbbf24', 
        '#92cc76', '#60a5fa', '#a78bfa', '#f59e0b'
      ];
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            const param = params[0];
            return `${param.name}<br/>Count: ${param.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: filteredFactors,
          axisLabel: {
            interval: 0
          }
        },
        series: [
          {
            name: 'Satisfaction Factor Count',
            type: 'bar',
            data: filteredValues.map((value, index) => ({
              value: value,
              itemStyle: {
                color: colors[index % colors.length]
              }
            })),
            label: {
              show: true,
              position: 'right'
            }
          }
        ]
      };
    },

    generateSentimentByDateChart() {
      // This is a stacked bar chart representing sentiment buckets over time
      const sentimentBuckets = [
        'Very Negative',
        'Negative',
        'Neutral', 
        'Positive',
        'Very Positive'
      ];
      
      const displayData = this.getDisplayData(this.trendingData.sentimentByDate);
      
      // Check if we have data
      if (!displayData || displayData.length === 0) {
        return {
          title: {
            text: 'No sentiment data available',
            left: 'center'
          }
        };
      }
      
      const colors = ['#dc2626', '#f87171', '#94a3b8', '#10b981', '#059669'];
      
      const series = sentimentBuckets.map((bucket, index) => {
        return {
          name: bucket,
          type: 'bar',
          stack: 'total',
          label: {
            show: false
          },
          data: displayData.map(item => {
            if (item.value && item.value[bucket] && item.value[bucket].calls) {
              return item.value[bucket].calls.length;
            }
            return 0;
          }),
          itemStyle: {
            color: colors[index % colors.length]
          }
        };
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: sentimentBuckets,
          type: 'scroll',
          orient: 'horizontal',
          left: 'center',
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        yAxis: {
          type: 'value'
        },
        xAxis: {
          type: 'category',
          data: displayData.map(item => this.getDisplayLabel(item.date, item.weekLabel)),
          axisLabel: {
            interval: 0,
            rotate: 45,
            show: displayData.length <= 10 ? true : false
          }
        },
        series
      };
    },

    generateSentimentByCategoryChart() {
      // This is a horizontal bar chart showing total calls by sentiment bucket
      const sentimentBuckets = [
        'Very Negative',
        'Negative', 
        'Neutral',
        'Positive',
        'Very Positive'
      ];
      
      // Check if we have data
      if (!this.trendingData.sentimentByCategory) {
        console.log('No sentimentByCategory data available');
        return {
          title: {
            text: 'No sentiment data available',
            left: 'center'
          }
        };
      }
      
      const colors = ['#dc2626', '#f87171', '#94a3b8', '#10b981', '#059669'];
      
      const values = sentimentBuckets.map(bucket => {
        const bucketData = this.trendingData.sentimentByCategory[bucket];
        return bucketData ? bucketData.count : 0;
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            const param = params[0];
            return `${param.name}<br/>Calls: ${param.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: sentimentBuckets,
          axisLabel: {
            interval: 0
          }
        },
        series: [
          {
            name: 'Call Count',
            type: 'bar',
            data: values.map((value, index) => ({
              value: value,
              itemStyle: {
                color: colors[index % colors.length]
              }
            })),
            label: {
              show: true,
              position: 'right'
            }
          }
        ]
      };
    },

    generateSentimentTrajectoriesByDateChart() {
      // This is a stacked bar chart representing sentiment trajectories over time
      
      const displayData = this.getDisplayData(this.trendingData.sentimentTrajectoriesByDate);
      
      // Check if we have data
      if (!displayData || displayData.length === 0) {
        return {
          title: {
            text: 'No sentiment trajectories data available',
            left: 'center'
          }
        };
      }
      
      // Get all unique trajectory types from the data
      const trajectoryTypes = new Set();
      displayData.forEach(item => {
        if (item.value) {
          Object.keys(item.value).forEach(trajectory => {
            trajectoryTypes.add(trajectory);
          });
        }
      });
      
      const trajectoryArray = Array.from(trajectoryTypes);
      
      // Generate colors for trajectories
      const colors = [
        '#5470c6', '#92cc76', '#fac858', '#ee6767', '#73c0de', '#3ba272', '#fc8453',
        '#9a60b4', '#ea7ccc', '#5fb3d3', '#b6e7ff', '#a0ca92'
      ];
      
      const series = trajectoryArray.map((trajectory, index) => {
        return {
          name: trajectory,
          type: 'bar',
          stack: 'total',
          label: {
            show: false
          },
          data: displayData.map(item => {
            if (item.value && item.value[trajectory] && item.value[trajectory].total !== undefined) {
              return item.value[trajectory].total;
            }
            return 0;
          }),
          itemStyle: {
            color: colors[index % colors.length]
          }
        };
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: trajectoryArray,
          type: 'scroll',
          orient: 'horizontal',
          left: 'center',
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        yAxis: {
          type: 'value'
        },
        xAxis: {
          type: 'category',
          data: displayData.map(item => this.getDisplayLabel(item.date, item.weekLabel)),
          axisLabel: {
            interval: 0,
            rotate: 45,
            show: displayData.length <= 10 ? true : false
          }
        },
        series
      };
    },

    generateSentimentTrajectoriesByCategoryChart() {
      // This is a horizontal bar chart showing total calls by sentiment trajectory
      
      // Check if we have data
      if (!this.trendingData.sentimentTrajectoriesByCategory) {
        console.log('No sentimentTrajectoriesByCategory data available');
        return {
          title: {
            text: 'No sentiment trajectories data available',
            left: 'center'
          }
        };
      }
      
      // Get trajectory data and filter/sort
      const trajectoryData = Object.entries(this.trendingData.sentimentTrajectoriesByCategory)
        .map(([trajectory, data]) => ({
          trajectory,
          count: data.count
        }))
        .filter(item => item.count > 0)
        .sort((a, b) => b.count - a.count);
      
      const trajectoryNames = trajectoryData.map(item => item.trajectory);
      const trajectoryCounts = trajectoryData.map(item => item.count);
      
      // Generate colors for trajectories
      const colors = [
        '#5470c6', '#92cc76', '#fac858', '#ee6767', '#73c0de', '#3ba272', '#fc8453',
        '#9a60b4', '#ea7ccc', '#5fb3d3', '#b6e7ff', '#a0ca92', '#fbc293', '#f4938e'
      ];
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            const param = params[0];
            return `${param.name}<br/>Calls: ${param.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: trajectoryNames,
          axisLabel: {
            interval: 0
          }
        },
        series: [
          {
            name: 'Call Count',
            type: 'bar',
            data: trajectoryCounts.map((count, index) => ({
              value: count,
              itemStyle: {
                color: colors[index % colors.length]
              }
            })),
            label: {
              show: true,
              position: 'right'
            }
          }
        ]
      };
    },

    generateCallDurationByDateChart() {
      // This is a stacked bar chart representing call duration buckets over time
      const durationBuckets = [
        '0-2', '2-4', '4-6', '6-8', '8-10', 
        '10-12', '12-14', '14-16', '16-18', '18-20', '20+'
      ];
      
      const displayData = this.getDisplayData(this.trendingData.callDurationByDate);
      
      // Check if we have data
      if (!displayData || displayData.length === 0) {
        return {
          title: {
            text: 'No call duration data available',
            left: 'center'
          }
        };
      }
      
      // Color gradient from green (short calls) to red (long calls)
      const colors = [
        '#22c55e', '#65a30d', '#84cc16', '#a3a3a3', '#facc15', 
        '#f59e0b', '#f97316', '#ef4444', '#dc2626', '#b91c1c', '#7f1d1d'
      ];
      
      const series = durationBuckets.map((bucket, index) => {
        return {
          name: bucket + ' min',
          type: 'bar',
          stack: 'total',
          label: {
            show: false
          },
          data: displayData.map(item => {
            if (item.value && item.value[bucket] && item.value[bucket].calls) {
              return item.value[bucket].calls.length;
            }
            return 0;
          }),
          itemStyle: {
            color: colors[index % colors.length]
          }
        };
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        legend: {
          data: durationBuckets.map(bucket => bucket + ' min'),
          type: 'scroll',
          orient: 'horizontal',
          left: 'center',
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        yAxis: {
          type: 'value'
        },
        xAxis: {
          type: 'category',
          data: displayData.map(item => this.getDisplayLabel(item.date, item.weekLabel)),
          axisLabel: {
            interval: 0,
            rotate: 45,
            show: displayData.length <= 10 ? true : false
          }
        },
        series
      };
    },

    generateCallDurationByCategoryChart() {
      // This is a horizontal bar chart showing total calls by duration bucket
      const durationBuckets = [
        '0-2', '2-4', '4-6', '6-8', '8-10', 
        '10-12', '12-14', '14-16', '16-18', '18-20', '20+'
      ];
      
      // Check if we have data
      if (!this.trendingData.callDurationByCategory) {
        console.log('No callDurationByCategory data available');
        return {
          title: {
            text: 'No call duration data available',
            left: 'center'
          }
        };
      }
      
      // Color gradient from green (short calls) to red (long calls)
      const colors = [
        '#22c55e', '#65a30d', '#84cc16', '#a3a3a3', '#facc15', 
        '#f59e0b', '#f97316', '#ef4444', '#dc2626', '#b91c1c', '#7f1d1d'
      ];
      
      const values = durationBuckets.map(bucket => {
        const bucketData = this.trendingData.callDurationByCategory[bucket];
        return bucketData ? bucketData.count : 0;
      });
      
      // Create labels with "min" suffix
      const bucketLabels = durationBuckets.map(bucket => bucket + ' min');
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            const param = params[0];
            return `${param.name}<br/>Calls: ${param.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value'
        },
        yAxis: {
          type: 'category',
          data: bucketLabels,
          axisLabel: {
            interval: 0
          }
        },
        series: [
          {
            name: 'Call Count',
            type: 'bar',
            data: values.map((value, index) => ({
              value: value,
              itemStyle: {
                color: colors[index % colors.length]
              }
            })),
            label: {
              show: true,
              position: 'right'
            }
          }
        ]
      };
    },

    generateRepeatCallersChart() {
      // This is a horizontal bar chart showing repeat callers (2+ calls)
      
      // Check if we have data
      if (!this.trendingData.repeatCallersProcessed || this.trendingData.repeatCallersProcessed.length === 0) {
        return {
          title: {
            text: 'No repeat callers data available',
            left: 'center'
          }
        };
      }
      
      // Get top 50 repeat callers to avoid overcrowding the chart
      const topRepeatCallers = this.trendingData.repeatCallersProcessed.slice(0, 50);
      
      const phoneNumbers = topRepeatCallers.map(([phoneNumber, callCount]) => 
        this.formatPhoneNumber(phoneNumber)
      );
      const callCounts = topRepeatCallers.map(([phoneNumber, callCount]) => callCount);
      
      // Generate a variety of colors for the bars
      const colors = [
        '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452',
        '#9a60b4', '#ea7ccc', '#5fb3d3', '#b6e7ff', '#a0ca92', '#fbc293', '#f4938e',
        '#b4e7ce', '#c4aef5', '#ea9fc7', '#8cd4e8', '#ffcc5c', '#ffa39e',
        '#85d8ce', '#c8a2c8', '#b4d5ff', '#c9dd22', '#fde047', '#fb7185',
        '#34d399', '#a78bfa', '#fbbf24', '#f87171', '#60a5fa', '#10b981',
        '#8b5cf6', '#f59e0b', '#ef4444', '#3b82f6', '#06b6d4', '#84cc16',
        '#f97316', '#ec4899', '#6366f1', '#8b5a2b', '#059669', '#dc2626',
        '#7c3aed', '#db2777', '#0891b2', '#65a30d', '#ca8a04', '#be185d'
      ];
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          formatter: function (params) {
            const param = params[0];
            return `${param.name}<br/>Calls: ${param.value}`;
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'value',
          name: 'Number of Calls'
        },
        yAxis: {
          type: 'category',
          data: phoneNumbers,
          axisLabel: {
            interval: 0,
            fontSize: 10
          },
          axisTick: {
            show: false
          }
        },
        series: [
          {
            name: 'Call Count',
            type: 'bar',
            data: callCounts.map((value, index) => ({
              value: value,
              itemStyle: {
                color: colors[index % colors.length]
              }
            })),
            label: {
              show: true,
              position: 'right',
              fontSize: 10
            }
          }
        ]
      };
    },

    generateSubCategoriesChart() {
      const subcategories = [
        'Account Balance & Bill Inquiries',
        'High Bill / Usage Inquiries',
        'Other Billing disputes',
        'Budget billing and equal payment plans',
        'Payment methods and options',
        'Payment issues',
        'Late payments, and collections',
        'Payment arrangements and extensions',
        'Payment Confirmation',
        'Pre-authorized payments',
        'Financial Assistance & Subsidies',
        'Security deposits (questions, refunds)',
        'Rate plan options and comparisons',
        'Understanding energy Usage & Consumption',
        'Energy efficiency programs and rebates',
        'Updating account information',
        'New account setup',
        'Account closure and termination',
        'Moves - service start/stop',
        'Merging/linking multiple accounts',
        'Deceased Account Management',
        'Landlord and tenants',
        'MyAccount Technical Support',
        'Service Requests',
        'Vegetation Management',
        'Power outages',
        'Power Quality & Reliability',
        'Safety concerns during outages',
        'Generator inquiries and safety',
        'Troubleshooting electrical issues',
        'Battery Loan',
        'Outage communicatons',
        'Distributed Energy Resources (DER)',
        'Privacy policy and data protection',
        'Feedback and suggestions',
        'Scam / Fraud',
        'Other'
      ];
      
      const values = subcategories.map(subcategory => {
        const data = this.trendingData.call_subcategories[subcategory];
        return this.ensureArrayData(data);
      });
      
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: [
          {
            type: 'category',
            data: subcategories,
            axisTick: {
              alignWithLabel: true
            }
          }
        ],
        yAxis: [
          {
            type: 'value'
          }
        ],
        series: [
          {
            name: 'Count',
            type: 'bar',
            barWidth: '60%',
            data: values
          }
        ]
      };
    },
    

    handleRepeatCallersChartClick(params) {
      // this.repeatCallersTable = {
      //   name: params.name,
      //   value: (this.trendingData.repeat_callers[params.name] || []).slice(0, 24)
      // }
    },
    // Method will be reimplemented when needed
    generateCustomerEffortAggregationChart() {
      // This will be updated based on the new data structure
      return {};
      

    },
    /* ---------- bubble-sort by array length (descending) ---------- */
    bubbleSortEntries(obj) {
      // This method is no longer used - keeping for reference
      const entries = Object.entries(obj);     // [ [key, value], … ]
      const n = entries.length;
      for (let i = 0; i < n - 1; i++) {
        for (let j = 0; j < n - i - 1; j++) {
          const left  = entries[j][1].length;
          const right = entries[j + 1][1].length;
          if (left < right) {                  // want bigger lengths first
            [entries[j], entries[j + 1]] = [entries[j + 1], entries[j]]; // swap
          }
        }
      }
      return entries;                          // sorted array
    },
    // Helper method to check if a string is a valid date in format yyyy-MM-dd
    isValidDateString(dateString) {
      if (!dateString) return false;
      
      // Check if it matches the format yyyy-MM-dd
      if (!dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return false;
      }
      
      // Try to parse it with Luxon
      try {
        const date = DateTime.fromFormat(dateString, 'yyyy-MM-dd');
        return date.isValid;
      } catch (e) {
        return false;
      }
    },
    // This method is no longer needed with the new data structure
    // but keeping it for now to avoid errors until all references are removed
    ensureArrayData(data) {
      if (Array.isArray(data)) {
        return data.length;
      } else if (typeof data === 'number') {
        return data;
      }
      return 0;
    },
    humanDate(date) {
      return DateTime.fromFormat(date, 'yyyy-MM-dd').toFormat('MMMM d, yyyy');
    },
    formatPhoneNumber(phoneNumber) {
      // Phone numbers are now pre-formatted in the database as (###) ###-####
      // Just return as-is if already formatted, otherwise try to format
      if (!phoneNumber) return '';
      
      // Check if already in correct format
      if (phoneNumber.match(/^\(\d{3}\) \d{3}-\d{4}$/)) {
        return phoneNumber;
      }
      
      // Fallback formatting for any legacy data
      const cleaned = phoneNumber.toString().replace(/\D/g, '');
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      } else if (cleaned.length === 11 && cleaned[0] === '1') {
        return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
      }
      return phoneNumber; // Return original if can't format
    },
    formatDateForTitle(date) {
      // Handle different date formats that might come from the charts
      if (!date) return 'Unknown Date';
      
      // If it's already in human-readable format (from chart labels), return as-is
      if (date.includes(',')) {
        return date;
      }
      
      // Try to parse as ISO date format (yyyy-MM-dd)
      try {
        const parsed = DateTime.fromFormat(date, 'yyyy-MM-dd');
        if (parsed.isValid) {
          return parsed.toFormat('MMMM d, yyyy');
        }
      } catch (e) {
        // Ignore parsing error, try next format
      }
      
      // Try to parse as human-readable format (in case it's already formatted)
      try {
        const parsed = DateTime.fromFormat(date, 'MMMM d, yyyy');
        if (parsed.isValid) {
          return date; // Already in correct format
        }
      } catch (e) {
        // Ignore parsing error
      }
      
      // If all parsing fails, return the original date
      console.warn('Could not format date for title:', date);
      return date;
    },
    applySelectedDateRange() {
      if (!this.selectedDateRange || this.selectedDateRange.length !== 2) {
        this.dateValidationMessage = 'Please select both start and end dates';
        return;
      }

      // Sort the dates to ensure proper order
      const sortedDates = [...this.selectedDateRange].sort();
      
      this.dateRange.start = sortedDates[0];
      this.dateRange.end = sortedDates[1];
      this.startDate = this.dateRange.start;
      this.endDate = this.dateRange.end;
      
      this.datePickerMenu = false; // Close the menu
      this.dateValidationMessage = '';
      this.getTrendsData();
    },
    onDateRangeChange(value) {
      this.selectedDateRange = value;
      
      // Auto-apply when both dates are selected
      if (value && value.length === 2) {
        this.applySelectedDateRange();
      }
    },
    
    toggleWeek() {
      // Toggle between daily and weekly view
      this.weeklyView = !this.weeklyView;
      this.regenerateCharts();
    },
    
    regenerateCharts() {
      // Force Vue reactivity by using $set to ensure charts update
      this.$set(this.trendingCharts, 'callVolume', this.generateCallVolumeChart());
      this.$set(this.trendingCharts, 'callMainCategoriesByDate', this.generateMainCategoriesByDateChart());
      this.$set(this.trendingCharts, 'callMainCategoriesByCategory', this.generateMainCategoriesByCategoryChart());
      this.$set(this.trendingCharts, 'callSubCategoriesByDate', this.generateSubCategoriesByDateChart());
      this.$set(this.trendingCharts, 'callSubCategoriesByCategory', this.generateSubCategoriesByCategoryChart());
      this.$set(this.trendingCharts, 'customerSatisfactionByDate', this.generateCustomerSatisfactionByDateChart());
      this.$set(this.trendingCharts, 'customerSatisfactionByCategory', this.generateCustomerSatisfactionByCategoryChart());
      this.$set(this.trendingCharts, 'sentimentByDate', this.generateSentimentByDateChart());
      this.$set(this.trendingCharts, 'sentimentByCategory', this.generateSentimentByCategoryChart());
      this.$set(this.trendingCharts, 'sentimentTrajectoriesByDate', this.generateSentimentTrajectoriesByDateChart());
      this.$set(this.trendingCharts, 'sentimentTrajectoriesByCategory', this.generateSentimentTrajectoriesByCategoryChart());
      this.$set(this.trendingCharts, 'callDurationByDate', this.generateCallDurationByDateChart());
      this.$set(this.trendingCharts, 'callDurationByCategory', this.generateCallDurationByCategoryChart());
      this.$set(this.trendingCharts, 'repeatCallers', this.generateRepeatCallersChart());
      
      // Reinitialize click detection after charts are updated
      this.$nextTick(() => {
        this.setupAdvancedClickDetection();
      });
    },
    
    aggregateDataByWeek(dailyData) {
      // Group daily data into weekly buckets
      if (!dailyData || dailyData.length === 0) {
        return [];
      }
      
      const weeklyData = {};
      
      dailyData.forEach((dayItem) => {
        // Parse the date and find the start of the week (Monday)
        const date = DateTime.fromFormat(dayItem.date, 'yyyy-MM-dd');
        const weekStart = date.startOf('week');
        const weekEnd = weekStart.plus({ days: 6 });
        const weekKey = weekStart.toFormat('yyyy-MM-dd');
        const weekLabel = `${weekStart.toFormat('MMM d')} - ${weekEnd.toFormat('MMM d, yyyy')}`;
        
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = {
            date: weekKey,
            weekLabel: weekLabel,
            value: 0 // Initialize as number for call volume
          };
        }
        
        // Aggregate the daily values into weekly totals
        if (dayItem.value && typeof dayItem.value === 'object') {
          // Handle complex objects (like categories with total and calls)
          if (typeof weeklyData[weekKey].value === 'number') {
            // Switch from number to object format
            weeklyData[weekKey].value = {};
          }
          
          Object.keys(dayItem.value).forEach(category => {
            if (!weeklyData[weekKey].value[category]) {
              weeklyData[weekKey].value[category] = {
                total: 0,
                calls: []
              };
            }
            
            const dayValue = dayItem.value[category];
            if (dayValue) {
              weeklyData[weekKey].value[category].total += dayValue.total || 0;
              if (dayValue.calls && Array.isArray(dayValue.calls)) {
                weeklyData[weekKey].value[category].calls.push(...dayValue.calls);
              }
            }
          });
        } else if (typeof dayItem.value === 'number') {
          // Handle simple numeric values (like call volume)
          weeklyData[weekKey].value = (weeklyData[weekKey].value || 0) + dayItem.value;
        }
      });
      
      // Convert to array and sort by date
      return Object.values(weeklyData).sort((a, b) => a.date.localeCompare(b.date));
    },
    
    getDisplayData(dailyData) {
      // Return either daily or weekly data based on current view mode
      if (this.weeklyView) {
        return this.aggregateDataByWeek(dailyData);
      }
      return dailyData;
    },
    
         getDisplayLabel(dateStr, weekLabel) {
       // Return appropriate label based on view mode
       if (this.weeklyView && weekLabel) {
         return weekLabel;
       }
       return this.humanDate(dateStr);
     },
     
     // Generate color shades for sub-categories based on their parent main category
     generateCategoryColorShades(baseColor, count) {
       // Convert hex to HSL for easier manipulation
       const hexToHsl = (hex) => {
         const r = parseInt(hex.slice(1, 3), 16) / 255;
         const g = parseInt(hex.slice(3, 5), 16) / 255;
         const b = parseInt(hex.slice(5, 7), 16) / 255;
         
         const max = Math.max(r, g, b);
         const min = Math.min(r, g, b);
         let h, s, l = (max + min) / 2;
         
         if (max === min) {
           h = s = 0;
         } else {
           const d = max - min;
           s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
           switch (max) {
             case r: h = (g - b) / d + (g < b ? 6 : 0); break;
             case g: h = (b - r) / d + 2; break;
             case b: h = (r - g) / d + 4; break;
           }
           h /= 6;
         }
         
         return [h * 360, s * 100, l * 100];
       };
       
       // Convert HSL back to hex
       const hslToHex = (h, s, l) => {
         h /= 360; s /= 100; l /= 100;
         const c = (1 - Math.abs(2 * l - 1)) * s;
         const x = c * (1 - Math.abs((h * 6) % 2 - 1));
         const m = l - c / 2;
         let r = 0, g = 0, b = 0;
         
         if (0 <= h && h < 1/6) {
           r = c; g = x; b = 0;
         } else if (1/6 <= h && h < 2/6) {
           r = x; g = c; b = 0;
         } else if (2/6 <= h && h < 3/6) {
           r = 0; g = c; b = x;
         } else if (3/6 <= h && h < 4/6) {
           r = 0; g = x; b = c;
         } else if (4/6 <= h && h < 5/6) {
           r = x; g = 0; b = c;
         } else if (5/6 <= h && h < 1) {
           r = c; g = 0; b = x;
         }
         
         r = Math.round((r + m) * 255);
         g = Math.round((g + m) * 255);
         b = Math.round((b + m) * 255);
         
         return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
       };
       
       const [h, s, l] = hexToHsl(baseColor);
       const shades = [];
       
       // Generate variations by adjusting lightness and saturation
       for (let i = 0; i < count; i++) {
         const factor = i / (count - 1); // 0 to 1
         
         // Create variation in lightness (darker to lighter)
         const newL = Math.max(20, Math.min(80, l + (factor - 0.5) * 40));
         // Slight variation in saturation for more variety
         const newS = Math.max(30, Math.min(100, s + (factor - 0.5) * 20));
         
         shades.push(hslToHex(h, newS, newL));
       }
       
       return shades;
     },
     
     // Get the parent main category for a sub-category
     getParentMainCategory(subCategory) {
       for (const [mainCategory, subCategories] of Object.entries(this.categoryMapping)) {
         if (subCategories.includes(subCategory)) {
           return mainCategory;
         }
       }
       return 'Other Inquiries'; // Fallback
     },
     
     // Generate color for a specific sub-category
     getSubCategoryColor(subCategory, allSubCategories) {
       const parentCategory = this.getParentMainCategory(subCategory);
       const baseColor = this.categoryColors[parentCategory];
       
       // Get all sub-categories for this parent
       const siblingSubCategories = this.categoryMapping[parentCategory] || [];
       const indexInParent = siblingSubCategories.indexOf(subCategory);
       
       if (indexInParent === -1) {
         // Fallback color if not found
         return '#6b7280';
       }
       
       // Generate shades for this parent category
       const shades = this.generateCategoryColorShades(baseColor, siblingSubCategories.length);
       return shades[indexInParent] || baseColor;
     },
  },
  created() {
    // Set default date range to last 30 days
    const now = DateTime.now().minus({ days: 2 }); // Use -2 days like in resetDateRange
    const defaultStart = now.minus({ days: 30 });
    
    // Format for display and store for query (using YYYY-MM-DD to match DB)
    this.dateRange.start = defaultStart.toFormat('yyyy-MM-dd');
    this.dateRange.end = now.toFormat('yyyy-MM-dd');
    this.startDate = this.dateRange.start;
    this.endDate = this.dateRange.end;
    
    // Set initial date range for the picker
    this.selectedDateRange = [this.dateRange.start, this.dateRange.end];
    
    console.log('Initial date range set in created():', this.dateRange.start, 'to', this.dateRange.end);
    
    // Load available dates from database
    this.loadAvailableDates();
    
    // Fetch data - will only be called once here
    this.getTrendsData();
  },
  watch: {
    $route: {
      handler: function(val, oldVal) {
        // Only fetch data if the route has actually changed
        if (oldVal && val.path !== oldVal.path) {
          this.getTrendsData();
        }
      },
      immediate: false
    }
  },
  computed: {
    isInformationalDialog() {
      // Check if the dialog contains informational messages rather than call IDs
      return this.callsDialog.calls.some(call => 
        typeof call === 'string' && (call.includes('📞') || call.includes('⚠️') || call.includes('data structure'))
      );
    },
    
    isExportingToInteractive() {
      // Computed property for loading state to ensure reactivity
      return this.exportingToInteractive;
    },
    
    totalCallsInRange() {
      // Calculate total calls from the callVolume data
      if (!this.trendingData || !this.trendingData.callVolume) {
        return 0;
      }
      return this.trendingData.callVolume.reduce((total, day) => total + (day.value || 0), 0);
    }
  },
  filters: {
    humanDate(date) {
      return DateTime.fromFormat(date, 'yyyy-MM-dd').toFormat('MMMM d, yyyy');
    },
    percentage(number) {
      return (number * 100).toFixed(1) + '%';
    },
    ellipsis(value, length) {
      if (!value) return '';
      if (value.length <= length) return value;
      return value.substring(0, length) + '...';
    },
    phoneNumber(number) {
      return number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
  },
}
</script>

<style lang="scss" scoped>
  .chart {
    height: 300px;
    cursor: pointer; // Make charts look clickable
  }
  
  // Improve hover states for better UX
  .v-sheet:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    transition: box-shadow 0.2s ease;
  }
</style>